import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from "recharts";
import { gsap } from "gsap";
import Sidebar, { clearSession } from "../components/Sidebar";
import { normalizeAccessLog } from "../utils/accessLogs";
import { isPhaseAdmin } from "../utils/role";
import { applyBillingEnumText, normalizeActivationAccount, normalizeCommunicationNote, normalizeMaintenanceRequest } from "../utils/enums";
import FullScreenImageViewer, { isImageAttachment } from "../components/FullScreenImageViewer";

const DEV_PROXY_BASE = "/api";
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = process.env.NODE_ENV === "development" ? DEV_PROXY_BASE : PROD_API_BASE;
const SESSION_KEY = "zangon_session";

function safeJsonParse(text) {
  try { return applyBillingEnumText(JSON.parse(text)); } catch { return null; }
}

// Reads every page of access logs matching `params` (a single page would cap the chart's totals).
async function fetchAllAccessLogPages(estateId, params = {}) {
  // The API accepts large pages; a 4-week window normally fits in one request.
  const PAGE = 2000;
  const all = [];
  for (let page = 1; page <= 50; page++) {
    const qp = new URLSearchParams({ ...params, pageIndex: String(page), pageSize: String(PAGE) });
    const res = await authFetch(`${API_BASE}/estates/${estateId}/access-logs?${qp.toString()}`);
    if (!res.ok) throw new Error(`Failed to load access logs (${res.status})`);
    const json = safeJsonParse(await res.text());
    const items = (Array.isArray(json?.data?.data) ? json.data.data : parseArrayData(json)).map(normalizeAccessLog);
    all.push(...items);
    const total = json?.data?.totalCount ?? 0;
    if (items.length < PAGE || all.length >= total) break;
  }
  return all;
}

// Access Analytics: every log from the start of the 4-week window, plus everyone still inside.
export const ACCESS_CHART_DAYS = 28;
async function fetchChartAccessLogs(estateId) {
  const since = new Date();
  since.setHours(0, 0, 0, 0);
  since.setDate(since.getDate() - (ACCESS_CHART_DAYS - 1));
  const [windowLogs, insideRes] = await Promise.all([
    fetchAllAccessLogPages(estateId, { startDate: since.toISOString() }),
    // Only the number of people inside is shown, so read totalCount off a one-row page.
    authFetch(`${API_BASE}/estates/${estateId}/access-logs?isActiveVisit=true&pageIndex=1&pageSize=1`),
  ]);
  const insideJson = insideRes.ok ? safeJsonParse(await insideRes.text()) : null;
  return { windowLogs, insideCount: insideJson?.data?.totalCount ?? 0 };
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getAccessToken() { return getSession()?.accessToken || ""; }
export function getRefreshToken() { return getSession()?.refreshToken || ""; }

export function saveLoginSession(payload) {
  setSession(payload);
  localStorage.setItem("auth_token", payload?.accessToken || "");
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    return res;
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally { clearTimeout(id); }
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const prev = getSession() || {};
  const next = { ...prev, ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

export const MAINTENANCE_CATEGORIES = [
  { value: "Access Control", label: "Access Control (gate pass, smart locks)", icon: "ki-lock", description: "Gate pass issues, smart lock faults, or access card problems." },
  { value: "Appliance Repair", label: "Appliance Repair (AC, fridge, etc.)", icon: "ki-switch", description: "Repairs for supplied appliances." },
  { value: "Billing / Payment", label: "Billing / Payment Issues", icon: "ki-wallet", description: "Issues with rent or payments." },
  { value: "CCTV / Surveillance", label: "CCTV / Surveillance", icon: "ki-eye", description: "Camera outages, playback requests, or surveillance issues." },
  { value: "Carpentry", label: "Carpentry (doors, wardrobes, cabinets)", icon: "ki-cube-2", description: "Repairs for doors, wardrobes, cabinets, and wooden structures." },
  { value: "Cleaning Services", label: "Cleaning Services (common areas)", icon: "ki-rescue", description: "Cleaning requests for common areas, hallways, or lobbies." },
  { value: "Delivery Issues", label: "Delivery Issues", icon: "ki-truck", description: "Lost or delayed deliveries." },
  { value: "Documentation", label: "Documentation Requests", icon: "ki-file", description: "Requests for official documents." },
  { value: "Drainage / Sewage", label: "Drainage / Sewage", icon: "ki-double-down", description: "Blocked drains, sewage backups, or plumbing emergencies." },
  { value: "Electrical", label: "Electrical", icon: "ki-electricity", description: "Issues with lighting, wiring, switches, or power outlets." },
  { value: "Elevator / Lift", label: "Elevator / Lift", icon: "ki-arrow-up-down", description: "Elevator malfunctions, servicing, or entrapment issues." },
  { value: "Fire Safety", label: "Fire Safety (alarms, extinguishers)", icon: "ki-yii", description: "Fire alarms, smoke detectors, or fire extinguisher checks." },
  { value: "General Inquiry", label: "General Inquiry", icon: "ki-information", description: "General questions or requests." },
  { value: "Generator / Power Supply", label: "Generator / Power Supply", icon: "ki-flash-circle", description: "Generator maintenance, faults, or power changeover issues." },
  { value: "HVAC / Air Conditioning", label: "HVAC / Air Conditioning", icon: "ki-abstract-14", description: "AC servicing, cooling issues, or ventilation repairs." },
  { value: "Internet / Cable Issues", label: "Internet / Cable Issues", icon: "ki-wifi", description: "Wi-Fi outages, slow internet, or cable TV problems." },
  { value: "Landscaping / Gardening", label: "Landscaping / Gardening", icon: "ki-abstract-38", description: "Lawn mowing, tree trimming, or garden maintenance." },
  { value: "Move-in / Move-out", label: "Move-in / Move-out Requests", icon: "ki-exit-right", description: "Move-in/out inspections or approvals." },
  { value: "Neighbor Disputes", label: "Neighbor Disputes", icon: "ki-people", description: "Conflicts between neighbors." },
  { value: "Noise Complaints", label: "Noise Complaints", icon: "ki-call", description: "Complaints regarding excessive noise." },
  { value: "Painting & Finishing", label: "Painting & Finishing", icon: "ki-brush", description: "Repainting walls, touch-ups, or surface finishing." },
  { value: "Parking Issues", label: "Parking Issues", icon: "ki-car", description: "Unauthorized parking or blocked driveways." },
  { value: "Pest Control", label: "Pest Control (rats, termites, mosquitoes)", icon: "ki-abstract-49", description: "Fumigation requests for pests." },
  { value: "Plumbing", label: "Plumbing", icon: "ki-wrench", description: "Issues with pipes, leaks, faucets, or drainage." },
  { value: "Security", label: "Security (guards, incidents, breaches)", icon: "ki-shield", description: "Security guard issues, incidents, or physical breaches." },
  { value: "Service Charge", label: "Service Charge Complaints", icon: "ki-document", description: "Service charge disputes." },
  { value: "Tiling / Flooring", label: "Tiling / Flooring", icon: "ki-grid", description: "Cracked tiles, flooring replacements, or grout repairs." },
  { value: "Visitor Management", label: "Visitor Management", icon: "ki-user", description: "Visitor check-in or access issues." },
  { value: "Waste Disposal", label: "Waste Disposal", icon: "ki-trash", description: "Missed trash pickup, waste bin requests, or hygiene issues." },
  { value: "Water Supply", label: "Water Supply / Borehole Issues", icon: "ki-drop", description: "No water, borehole issues, or water pressure problems." },
];

export const getWeatherDescription = (code) => {
  if (code === 0) return { text: "Clear Sky", icon: "ki-sun text-warning", emoji: "☀️" };
  if (code >= 1 && code <= 3) return { text: "Partly Cloudy", icon: "ki-cloud text-white", emoji: "⛅" };
  if (code >= 45 && code <= 48) return { text: "Foggy", icon: "ki-abstract-26 text-white", emoji: "🌫️" };
  if (code >= 51 && code <= 67) return { text: "Rainy", icon: "ki-drop text-info", emoji: "🌧️" };
  if (code >= 80 && code <= 82) return { text: "Showers", icon: "ki-drop text-info", emoji: "🌦️" };
  if (code >= 95) return { text: "Thunderstorm", icon: "ki-flash text-warning", emoji: "🌩️" };
  return { text: "Partly Cloudy", icon: "ki-cloud text-white", emoji: "⛅" };
};

export const generateWeatherRemark = (forecast) => {
  if (!forecast || forecast.length === 0) {
    return "Pleasant weather expected across Kado, Abuja with moderate temperatures and fair skies.";
  }
  const todayItem = forecast[0];
  const maxT = todayItem.maxTemp;
  const label = (todayItem.label || "").toLowerCase();

  if (label.includes("rain") || label.includes("shower") || label.includes("thunder")) {
    return "Rain expected in Kado today. Plan outdoor maintenance accordingly.";
  }
  if (maxT >= 32) {
    return "Hot conditions ahead in Kado today. Ensure estate cooling systems are operational.";
  }
  if (maxT >= 27) {
    return "Warm & pleasant weather in Kado today. Optimal for routine outdoor maintenance.";
  }
  return "Cool & fair weather across Kado, Abuja today. Ideal conditions for estate operations.";
};

export const getCategoryIconMeta = (cat) => {
  if (!cat) return { icon: "ki-wrench", bg: "bg-light-primary text-primary" };
  const cLower = cat.toLowerCase();
  const found = MAINTENANCE_CATEGORIES.find(
    (c) => c.value.toLowerCase() === cLower || c.label.toLowerCase().includes(cLower) || cLower.includes(c.value.toLowerCase())
  );
  if (found) {
    let bg = "bg-light-primary text-primary";
    const val = found.value.toLowerCase();
    if (val.includes("plumb") || val.includes("water") || val.includes("drain")) bg = "bg-light-info text-info";
    else if (val.includes("electric") || val.includes("hvac") || val.includes("appliance") || val.includes("power") || val.includes("generator")) bg = "bg-light-warning text-warning";
    else if (val.includes("cleaning") || val.includes("waste") || val.includes("landscaping")) bg = "bg-light-success text-success";
    return { icon: found.icon, bg };
  }
  if (cLower.includes("plumb") || cLower.includes("pipe") || cLower.includes("leak")) return { icon: "ki-wrench", bg: "bg-light-info text-info" };
  if (cLower.includes("water") || cLower.includes("borehole")) return { icon: "ki-drop", bg: "bg-light-info text-info" };
  if (cLower.includes("hvac") || cLower.includes("air") || cLower.includes("ac")) return { icon: "ki-abstract-14", bg: "bg-light-warning text-warning" };
  if (cLower.includes("electric") || cLower.includes("wiring") || cLower.includes("light")) return { icon: "ki-electricity", bg: "bg-light-warning text-warning" };
  if (cLower.includes("sec") || cLower.includes("guard")) return { icon: "ki-shield", bg: "bg-light-primary text-primary" };
  if (cLower.includes("lock") || cLower.includes("gate")) return { icon: "ki-lock", bg: "bg-light-primary text-primary" };
  if (cLower.includes("clean") || cLower.includes("hygiene")) return { icon: "ki-rescue", bg: "bg-light-success text-success" };
  if (cLower.includes("trash") || cLower.includes("waste")) return { icon: "ki-trash", bg: "bg-light-success text-success" };
  return { icon: "ki-wrench", bg: "bg-light-primary text-primary" };
};

function CategoryDropdown({ value, onChange, placeholder = "Select Category..." }) {
  const [searchTerm, setSearchTerm] = useState("");
  const selectedCategory = MAINTENANCE_CATEGORIES.find((c) => c.value === value);
  const selectedIconMeta = getCategoryIconMeta(value || selectedCategory?.value);
  const filteredCategories = MAINTENANCE_CATEGORIES.filter((c) =>
    c.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="dropdown w-100">
      <button
        className="form-select form-select-sm text-start d-flex align-items-center justify-content-between w-100 bg-white"
        type="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        data-bs-auto-close="outside"
      >
        <span className="d-flex align-items-center text-truncate pe-2">
          {selectedCategory || value ? (
            <>
              <i className={`ki-outline ${selectedIconMeta.icon} fs-5 text-success me-2 flex-shrink-0`}></i>
              <span className="text-gray-800">{selectedCategory ? selectedCategory.label : value}</span>
            </>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </span>
      </button>

      <div className="dropdown-menu w-100 p-3 border-gray-200">
        <div className="position-relative mb-2">
          <i className="ki-outline ki-magnifier fs-6 position-absolute top-50 translate-middle-y ms-3 text-gray-500"></i>
          <input
            type="text"
            className="form-control form-control-sm ps-9 bg-light border-0"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <ul className="list-unstyled mb-0 py-2 mh-250px overflow-y-auto">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => (
              <li key={category.value}>
                <button
                  type="button"
                  className={`dropdown-item d-flex align-items-center py-2 px-4 ${value === category.value ? "bg-light-success text-success" : "text-gray-700"}`}
                  onClick={() => {
                    onChange(category.value);
                    setSearchTerm("");
                  }}
                >
                  <i className={`ki-outline ${category.icon} fs-5 me-3 ${value === category.value ? "text-success" : "text-gray-500"}`}></i>
                  <span className="fs-7 fw-semibold">{category.label}</span>
                  {value === category.value && <i className="ki-outline ki-check fs-6 text-success ms-auto"></i>}
                </button>
              </li>
            ))
          ) : (
            <li className="text-center text-muted fs-8 py-3">No categories found</li>
          )}
        </ul>
      </div>
    </div>
  );
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      token = renewed.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

async function uploadAdminImage(imageFile, adminId) {
  if (!imageFile || !adminId) return null;
  try {
    const formData = new FormData();
    formData.append("file", imageFile);
    const res = await authFetch(`${API_BASE}/admins/${adminId}/uploads`, { method: "POST", headers: {}, body: formData });
    const json = safeJsonParse(await res.text());
    if (!res.ok) return null;
    if (typeof json?.data !== "string" || !json.data.startsWith("http")) return null;
    return json.data;
  } catch {
    return null;
  }
}

function capitalizeFirst(str) {
  const t = String(str || "").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning 🌤️";
  if (h < 17) return "Good Afternoon ☀️";
  return "Good Evening 🌑";
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  });
}

function parseISODate(iso) {
  if (!iso) return new Date();
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function daysBetween(a, b) {
  const ms = 24 * 60 * 60 * 1000;
  const a0 = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const b0 = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.floor((b0 - a0) / ms);
}

const HOUSE_TYPE_IMAGES = {
  "1 Bedroom Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "1 Bedrooms Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "2 Bedrooms Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "3 Bedrooms Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedrooms Duplex": "/assets/media/zangon/4bd.jpg",
  "5 Bedrooms Terrace": "/assets/media/zangon/5-light.jpg",
  "3 Bedroom Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedroom Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "4 Bedrooms Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "6 Bedroom Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedrooms Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedroom Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
  "6 Bedrooms Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
};

function getHouseImage(typeName) {
  if (!typeName) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  const norm = typeName.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/bedrooms?/g, "bedroom");
  if (norm.includes("1bedroom") || norm.includes("1bd")) return "/assets/media/zangon/1bedroombungalo.jpg";
  if (norm.includes("2bedroom") || norm.includes("2bd")) return "/assets/media/zangon/2bedroom.jpg";
  if (norm.includes("3bedroombungalow") || norm.includes("3bedroombungalo") || norm.includes("3bd")) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  if (norm.includes("4bedroombungalow") || norm.includes("4bedroombungalo")) return "/assets/media/zangon/4bedroombungalo.jpg";
  if (norm.includes("5bedroom")) return "/assets/media/zangon/5-light.jpg";
  if (norm.includes("6bedroombungalow")) return "/assets/media/zangon/6bedroombungalow.jpg";
  if (norm.includes("6bedroomterraceduplex") || norm.includes("6bedroomterraceduple") || (norm.includes("6bedroom") && norm.includes("terrace"))) return "/assets/media/zangon/6bedroomterraceduple.jpg";
  return HOUSE_TYPE_IMAGES[typeName] || "/assets/media/zangon/3bedroomsbungalo.jpg";
}

function getEmptyStateImg() {
  return "/assets/media/zangon/5-dark.jpg";
}

function getDueMeta(today, expiryISO) {
  const d = daysBetween(today, parseISODate(expiryISO));
  const plural = (n) => `${n} day${n === 1 ? "" : "s"}`;
  if (d < 0) return { badgeText: "Overdue", badgeClass: "badge-light-danger", expiryClass: "text-danger", days: `${plural(Math.abs(d))} overdue` };
  if (d <= 30) return { badgeText: "Due Soon", badgeClass: "badge-light-warning", expiryClass: "text-warning", days: `${plural(d)} left` };
  return { badgeText: "Not Due", badgeClass: "badge-light-success", expiryClass: "text-success", days: `${plural(d)} left` };
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const AVATAR_CLASSES = [
  "bg-light-success text-success",
  "bg-light-danger text-danger",
  "bg-light-primary text-primary",
  "bg-light-warning text-warning",
  "bg-light-info text-info",
];

function getInitials(first = "", last = "") {
  if (first && last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  const clean = (first || last || "").replace(/^(mr|mrs|ms|miss|dr|prof|engr|arch|barr|alh|alhaji)\.?\s+/i, "").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (clean.slice(0, 2) || "??").toUpperCase();
}

function useBootstrapModal(ref) {
  const show = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
  }, [ref]);
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { show, hide };
}

function PagePreloader({ visible }) {
  return (
    <div
      className={`page-preloader-overlay ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

const RentListSkeleton = () => (
  <div className="d-flex align-items-center mb-3 p-4 d6-list-card border border-gray-100 placeholder-glow">
    <div className="symbol symbol-45px me-5">
      <span className="symbol-label bg-secondary placeholder"></span>
    </div>
    <div className="d-flex flex-column flex-grow-1 gap-2">
      <span className="placeholder rounded bg-secondary opacity-50 w-50"></span>
      <span className="placeholder rounded bg-secondary opacity-25 w-25"></span>
      <span className="placeholder rounded-pill bg-secondary opacity-25 w-25 mt-1"></span>
    </div>
    <div className="d-flex gap-8 text-end ms-6">
      <div className="d-flex flex-column gap-2 align-items-end">
        <span className="placeholder rounded bg-secondary opacity-25 w-50px"></span>
        <span className="placeholder rounded bg-secondary opacity-50 w-75px"></span>
      </div>
      <div className="d-flex flex-column gap-2 align-items-end">
        <span className="placeholder rounded bg-secondary opacity-25 w-50px"></span>
        <span className="placeholder rounded bg-secondary opacity-50 w-75px"></span>
      </div>
    </div>
  </div>
);

const RequestListSkeleton = () => (
  <div className="d-flex align-items-center position-relative mb-3 p-4 d6-list-card border border-gray-100 placeholder-glow overflow-hidden">
    <div className="position-absolute top-0 start-0 rounded h-100 bg-secondary w-4px"></div>
    <div className="symbol symbol-45px symbol-circle me-5 ms-2 flex-shrink-0">
      <span className="symbol-label bg-secondary placeholder rounded-circle w-45px h-45px"></span>
    </div>
    <div className="d-flex flex-column flex-grow-1 gap-2">
      <span className="placeholder rounded bg-secondary opacity-50 w-50"></span>
      <span className="placeholder rounded bg-secondary opacity-25 w-25"></span>
    </div>
  </div>
);

const SUCCESS_DOTS = [
  { top: "18%", left: "14%", size: 6, delay: 0 },
  { top: "70%", left: "10%", size: 5, delay: 0.3 },
  { top: "82%", left: "34%", size: 4, delay: 0.6 },
  { top: "12%", left: "80%", size: 5, delay: 0.2 },
  { top: "74%", left: "88%", size: 6, delay: 0.5 },
  { top: "90%", left: "62%", size: 4, delay: 0.8 },
];

function SuccessState({ title, message, duration = 2400, onDone }) {
  const doneRef = useRef(onDone);
  useEffect(() => {
    doneRef.current = onDone;
  });
  useEffect(() => {
    const t = setTimeout(() => doneRef.current?.(), duration);
    return () => clearTimeout(t);
  }, [duration]);

  return (
    <div className="d6-success" role="status" aria-live="polite">
      <div className="d6-success-burst">
        <span className="d6-success-ring d6-success-ring-lg" />
        <span className="d6-success-ring d6-success-ring-md" />
        {SUCCESS_DOTS.map((dot, i) => (
          <span
            key={i}
            className="d6-success-dot"
            style={{ top: dot.top, left: dot.left, width: dot.size, height: dot.size, animationDelay: `${dot.delay}s` }}
          />
        ))}
        <svg viewBox="0 0 120 120" className="d6-success-check" aria-hidden="true">
          <circle cx="60" cy="60" r="52" className="d6-success-halo" />
          <circle cx="60" cy="60" r="44" className="d6-success-fill" />
          <circle cx="60" cy="60" r="44" className="d6-success-stroke" />
          <path d="M40 61 L54 75 L81 46" className="d6-success-tick" />
        </svg>
      </div>
      <h3 className="d6-success-title">{title}</h3>
      {message && <p className="d6-success-msg">{message}</p>}
      <div className="d6-success-progress">
        <span style={{ animationDuration: `${duration}ms` }} />
      </div>
    </div>
  );
}

function ModalShell({ modalRef, icon, title, onClose, closeDisabled, subheader, footer, overlay, width = 720, fill = false, children }) {
  return (
    <div className="modal fade" ref={modalRef} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered d6m-dialog" style={{ maxWidth: width }}>
        <div className="modal-content d6m">
          {overlay}
          <div className="d6m-head">
            <span className="d6m-head-icon"><i className={`ki-outline ${icon}`}></i></span>
            <div className="min-w-0 flex-grow-1">
              <h3 className="d6m-title text-truncate">{title}</h3>
            </div>
            <button type="button" className="d6m-x" onClick={onClose} disabled={closeDisabled} aria-label="Close">
              <i className="ki-outline ki-cross"></i>
            </button>
          </div>
          {subheader}
          <div className={`d6m-body ${fill ? "d6m-body-fill" : ""}`}>{children}</div>
          {footer && <div className="d6m-foot">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ color = "#a1a1aa", children }) {
  return (
    <span className="d6m-chip" style={{ "--dot": color }}>
      {children}
    </span>
  );
}

function Avatar({ src, name, size = 48, onClick }) {
  const initials = getInitials(name || "");
  return (
    <span
      className={`d6m-avatar ${onClick ? "cursor-pointer" : ""}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
      onClick={onClick}
      title={onClick ? "View photo" : undefined}
    >
      {src ? <img src={src} alt="" /> : initials}
    </span>
  );
}

const DUE_DOT = { Overdue: "#DC2626", "Due Soon": "#D97706", "Not Due": "#16A34A" };

function invoiceDot(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("paid")) return "#16A34A";
  if (s.includes("overdue")) return "#DC2626";
  return "#D97706";
}

const Shimmer = ({ w = "100%", h = 12, r = 6, className = "" }) => (
  <span className={`d6m-shimmer ${className}`} style={{ width: w, height: h, borderRadius: r }} />
);

const PersonSkeleton = () => (
  <div className="d6m-person">
    <Shimmer w={48} h={48} r={999} />
    <div className="flex-grow-1 d-flex flex-column gap-2">
      <Shimmer w="45%" h={14} />
      <Shimmer w="30%" h={10} />
    </div>
    <Shimmer w={72} h={24} r={999} />
  </div>
);

const RentInvoiceSkeleton = () => (
  <>
    <PersonSkeleton />
    <div className="d6m-kv mt-5">
      {[1, 2].map((n) => (
        <div key={n} className="d6m-kv-cell">
          <Shimmer w="40%" h={10} />
          <Shimmer w="60%" h={14} className="mt-2" />
        </div>
      ))}
    </div>
    <div className="d6m-section">
      <Shimmer w="35%" h={12} />
      <div className="d6m-invoice mt-3">
        <Shimmer w="40%" h={18} />
        <Shimmer w="25%" h={10} className="mt-2" />
        <div className="d6m-lines mt-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="d6m-line"><Shimmer w="30%" h={10} /><Shimmer w="20%" h={10} /></div>
          ))}
        </div>
      </div>
    </div>
  </>
);

function TenantDetailModal({ modalRef, tenant, today, onClose, onViewFull }) {
  const { hide } = useBootstrapModal(modalRef);
  const handleClose = () => { hide(); onClose?.(); };

  const due = tenant?.expiryISO ? getDueMeta(today, tenant.expiryISO) : null;
  const invoices = tenant?.outstandingInvoices || [];

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-bill"
      title="Rent invoice"
      onClose={handleClose}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>Close</button>
          <button
            type="button"
            className="btn btn-success d6-mbtn"
            disabled={!tenant}
            onClick={() => { handleClose(); onViewFull?.(tenant); }}
          >
            View rent history
          </button>
        </>
      }
    >
      {!tenant ? (
        <RentInvoiceSkeleton />
      ) : (
        <>
          <div className="d6m-person">
            <Avatar src={tenant.imageUrl} name={tenant.name} />
            <div className="min-w-0 flex-grow-1">
              <div className="d6m-name text-truncate">{tenant.name}</div>
              <div className="d6m-muted text-truncate">
                {tenant.isCorporate ? "Corporate tenant" : "Individual tenant"}
                {tenant.phone ? ` · ${tenant.phone}` : ""}
              </div>
            </div>
            {due && <StatusChip color={DUE_DOT[due.badgeText]}>{due.badgeText}</StatusChip>}
          </div>

          <div className="d6m-kv mt-5">
            <div className="d6m-kv-cell">
              <span className="d6m-k">Time left</span>
              <span className="d6m-v">{due ? due.days : "—"}</span>
            </div>
            <div className="d6m-kv-cell">
              <span className="d6m-k">Expiry date</span>
              <span className="d6m-v">{tenant.expiryLabel || "—"}</span>
            </div>
          </div>

          <div className="d6m-section">
            <div className="d6m-section-head">
              <span>Outstanding invoices</span>
              <span className="d6m-count">{invoices.length}</span>
            </div>
            {invoices.length === 0 ? (
              <div className="d6m-empty">No outstanding invoices for this unit.</div>
            ) : (
              invoices.map((inv, i) => {
                const comps = Array.isArray(inv.components) ? inv.components : [];
                const compTotal = comps.reduce((a, c) => a + Number(c.amount || 0), 0);
                const total = Number(inv.totalAmount ?? inv.rentAmount ?? compTotal) || compTotal;
                return (
                  <div key={i} className="d6m-invoice">
                    <div className="d-flex align-items-start justify-content-between gap-3">
                      <div>
                        <div className="d6m-amount">₦{total.toLocaleString()}</div>
                        <div className="d6m-muted mt-1">Due {fmtDate(inv.dueDate)}</div>
                      </div>
                      <StatusChip color={invoiceDot(inv.status)}>{inv.status || "Pending"}</StatusChip>
                    </div>
                    {comps.length > 0 && (
                      <div className="d6m-lines">
                        {comps.map((c, ci) => (
                          <div key={ci} className="d6m-line">
                            <span>{c.displayName || c.name || c.type || "Charge"}</span>
                            <span>₦{Number(c.amount || 0).toLocaleString()}</span>
                          </div>
                        ))}
                        <div className="d6m-line d6m-line-total">
                          <span>Total due</span>
                          <span>₦{total.toLocaleString()}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="d6m-section">
            <div className="d6m-section-head"><span>Property</span></div>
            <div className="d6m-property">
              <img
                src={tenant.buildingImageUrl || getHouseImage(tenant.typeName)}
                alt=""
                onError={(e) => { e.target.onerror = null; e.target.src = "/assets/media/zangon/3bedroomsbungalo.jpg"; }}
              />
              <div className="min-w-0 flex-grow-1">
                <div className="d6m-v text-truncate">{tenant.typeName || "Residential unit"}</div>
                <div className="d6m-muted text-truncate">
                  {[tenant.phaseName, tenant.buildingNumber && `Unit ${tenant.buildingNumber}`].filter(Boolean).join(" · ") || "No unit assigned"}
                </div>
              </div>
              <div className="text-end flex-shrink-0">
                <div className="d6m-v">{tenant.basicRentFormatted || tenant.amount || "—"}</div>
                <div className="d6m-muted">Basic rent</div>
              </div>
            </div>
          </div>
        </>
      )}
    </ModalShell>
  );
}

const PRIORITY_DOT = { Low: "#0891B2", Normal: "#a1a1aa", High: "#D97706", Emergency: "#DC2626" };
const REQUEST_STATUS_DOT = { Pending: "#D97706", Completed: "#16A34A", Canceled: "#DC2626" };
const STATUS_TO_INT = { Created: 0, "In Progress": 1, Completed: 2, Canceled: 3 };
const STATUS_SUCCESS_COPY = {
  Completed: { title: "Issue resolved", message: "The request is marked completed and the resident can see it's been handled." },
  Canceled: { title: "Request canceled", message: "The request has been closed without action. The resident can see the update." },
  Pending: { title: "Request reopened", message: "The request is back in the queue and marked as pending." },
};

function normalizeRequestStatus(st) {
  const s = (st || "").toLowerCase();
  if (s.includes("complete") || s.includes("resolve")) return "Completed";
  if (s.includes("cancel") || s.includes("reject")) return "Canceled";
  return "Pending";
}

const RequestDetailSkeleton = () => (
  <>
    <PersonSkeleton />
    <div className="d6m-kv d6m-kv-4 mt-5">
      {[1, 2, 3, 4].map((n) => (
        <div key={n} className="d6m-kv-cell">
          <Shimmer w="50%" h={10} />
          <Shimmer w="70%" h={14} className="mt-2" />
        </div>
      ))}
    </div>
    <div className="d6m-section">
      <Shimmer w="30%" h={12} />
      <Shimmer w="100%" h={12} className="mt-3" />
      <Shimmer w="85%" h={12} className="mt-2" />
      <Shimmer w="60%" h={12} className="mt-2" />
    </div>
    <div className="d6m-section">
      <Shimmer w="30%" h={12} />
      <Shimmer w="100%" h={40} r={999} className="mt-3" />
    </div>
  </>
);

function RequestDetailModal({ modalRef, estateId, request, onClose, onStatusChange }) {
  const [fullRequest, setFullRequest] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("details");
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [success, setSuccess] = useState(null);
  const [fullscreenViewer, setFullscreenViewer] = useState({ isOpen: false, images: [], initialIndex: 0, title: "Maintenance Attachment" });
  const notesEndRef = useRef(null);
  const fetchedIdRef = useRef(null);

  useEffect(() => {
    if (!request?.id || !estateId) return;
    if (fetchedIdRef.current === request.id) return;
    fetchedIdRef.current = request.id;
    setLoading(true);
    setTab("details");
    setFullRequest(request);
    setNotes([]);

    Promise.all([
      authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}`).then((r) => (r.ok ? r.json() : null)),
      authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/communication-notes?pageIndex=1&pageSize=100`).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([reqData, notesData]) => {
        const fd = reqData?.data || {};
        let modalBuilding = fd?.buildingNumber || request?.buildingNumber || "Unassigned";
        if (fd?.buildingNumber && fd?.requesterCurrentBuildings) {
          const b = fd.requesterCurrentBuildings.find((x) => x.number === fd.buildingNumber);
          if (b?.phaseName) modalBuilding = `${b.phaseName} - ${fd.buildingNumber}`;
        } else if (request?.buildingNumber?.includes("—")) {
          modalBuilding = request.buildingNumber;
        }
        setFullRequest({
          ...(request || {}),
          ...normalizeMaintenanceRequest(fd),
          title: capitalizeFirst(fd?.title || request?.title || ""),
          description: capitalizeFirst(fd?.description || request?.description || ""),
          tenantTitle: fd?.requesterTitle || request?.requesterTitle || request?.tenantTitle || null,
          tenantName: fd?.requesterName || request?.requesterName || request?.tenantName || "Unknown",
          tenantProfilePictureUrl: fd?.requesterImageUrl || request?.requesterImageUrl || request?.tenantProfilePictureUrl || null,
          buildingNumber: modalBuilding,
        });
        setNotes(parseArrayData(notesData).map(normalizeCommunicationNote));
      })
      .catch(() => toast.error("Failed to load full request details."))
      .finally(() => setLoading(false));
  }, [request, estateId]);

  useEffect(() => {
    if (tab === "thread" && notes.length) notesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [notes, tab]);

  const handleClose = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
    setReplyText("");
    setNotes([]);
    setSuccess(null);
    fetchedIdRef.current = null;
    onClose?.();
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/communication-notes`,
        { method: "POST", body: JSON.stringify({ message: replyText.trim() }) }
      );
      if (!res.ok) throw new Error("Failed to send reply.");
      setReplyText("");
      const updated = await authFetch(
        `${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/communication-notes?pageIndex=1&pageSize=100`
      );
      if (updated.ok) setNotes(parseArrayData(await updated.json()).map(normalizeCommunicationNote));
      setJustSent(true);
      setTimeout(() => setJustSent(false), 1800);
    } catch (err) {
      toast.error(err.message || "Failed to send reply.");
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!newStatus || newStatus === normalizeRequestStatus(fullRequest?.status)) return;
    setUpdatingStatus(true);
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/maintenance-requests/${request.id}/status`,
        { method: "PATCH", body: JSON.stringify({ status: STATUS_TO_INT[newStatus] ?? 0 }) }
      );
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(safeJsonParse(errText)?.message || "Failed to update status.");
      }
      setFullRequest((prev) => ({ ...prev, status: newStatus }));
      onStatusChange?.(request.id, newStatus);
      setSuccess(STATUS_SUCCESS_COPY[newStatus] || STATUS_SUCCESS_COPY.Pending);
    } catch (err) {
      toast.error(err.message || "Failed to update status.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const d = fullRequest || request;
  const status = normalizeRequestStatus(d?.status);
  const priority = d?.priorityLevel || d?.priority || "Normal";
  const avatarImg = d ? d.tenantProfilePictureUrl || d.requesterImageUrl || d.tenantAvatar || d.imageUrl : null;
  const residentName = (() => {
    if (!d) return "Resident";
    const tName = d.requesterName || d.tenantName || "";
    const tTitle = d.requesterTitle || d.tenantTitle || d.titlePrefix;
    if (!tName) return "Resident";
    if (tTitle && !tName.toLowerCase().startsWith(tTitle.toLowerCase())) return `${tTitle} ${tName}`;
    return tName;
  })();
  const titleText = (d?.title || d?.maintenanceTitle || d?.shortDescription || "").trim();
  const descText = (d?.description || "").trim();
  const showDesc = descText && descText.toLowerCase() !== titleText.toLowerCase();
  const attachments = Array.isArray(d?.uploadedFileUrls) ? d.uploadedFileUrls : [];
  const imageAttachments = attachments.filter(isImageAttachment);

  return (
    <>
      <ModalShell
        modalRef={modalRef}
        icon="ki-wrench"
        title={titleText || "Maintenance request"}
        onClose={handleClose}
        width={760}
        fill={tab === "thread"}
        overlay={success && <SuccessState title={success.title} message={success.message} onDone={() => setSuccess(null)} />}
        subheader={
          d && (
            <div className="d6m-tabs">
              <button type="button" className={tab === "details" ? "active" : ""} onClick={() => setTab("details")}>Details</button>
              <button type="button" className={tab === "thread" ? "active" : ""} onClick={() => setTab("thread")}>
                Conversation{notes.length > 0 && <span className="d6m-count ms-2">{notes.length}</span>}
              </button>
            </div>
          )
        }
        footer={<button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>Close</button>}
      >
        {!d ? (
          <div className="d6m-empty">No request selected.</div>
        ) : tab === "details" ? (
          loading ? (
            <RequestDetailSkeleton />
          ) : (
            <>
              <div className="d6m-person">
                <Avatar
                  src={avatarImg}
                  name={residentName}
                  onClick={avatarImg ? () => setFullscreenViewer({ isOpen: true, images: [avatarImg], initialIndex: 0, title: `${residentName} - Profile Photo` }) : undefined}
                />
                <div className="min-w-0 flex-grow-1">
                  <div className="d6m-name text-truncate">{residentName}</div>
                  <div className="d6m-muted text-truncate">{d.tenantPhone || d.phoneNumber || d.phone || d.buildingNumber || "Resident"}</div>
                </div>
                <StatusChip color={REQUEST_STATUS_DOT[status]}>{status}</StatusChip>
              </div>

              <div className="d6m-kv d6m-kv-4 mt-5">
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Created</span>
                  <span className="d6m-v">{fmtDate(d.createdDate || d.meta)}</span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">{status === "Completed" ? "Completed" : "Last updated"}</span>
                  <span className="d6m-v">{fmtDate(d.lastModified || d.completedDate || d.createdDate)}</span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Priority</span>
                  <span className="d6m-v d-flex align-items-center gap-2">
                    <span className="d6m-dot" style={{ background: PRIORITY_DOT[priority] || "#a1a1aa" }} />
                    {priority}
                  </span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Category</span>
                  <span className="d6m-v text-truncate">{d.category || "General"}</span>
                </div>
              </div>

              <div className="d6m-section">
                <div className="d6m-section-head"><span>Issue</span></div>
                <div className="d6m-issue-title">{titleText || "No description provided."}</div>
                {showDesc && <p className="d6m-issue-desc">{descText}</p>}
              </div>

              {attachments.length > 0 && (
                <div className="d6m-section">
                  <div className="d6m-section-head">
                    <span>Attachments</span>
                    <span className="d6m-count">{attachments.length}</span>
                  </div>
                  <div className="d6m-attachments">
                    {attachments.map((url, i) =>
                      isImageAttachment(url) ? (
                        <button
                          key={i}
                          type="button"
                          className="d6m-thumb"
                          onClick={() =>
                            setFullscreenViewer({
                              isOpen: true,
                              images: imageAttachments.length > 0 ? imageAttachments : [url],
                              initialIndex: Math.max(0, imageAttachments.indexOf(url)),
                              title: titleText ? `Attachment: ${titleText}` : `Attachment ${i + 1}`,
                            })
                          }
                          title="View full screen"
                        >
                          <img src={url} alt={`Attachment ${i + 1}`} />
                        </button>
                      ) : (
                        <a key={i} href={url} target="_blank" rel="noreferrer" className="d6m-file">
                          <i className="ki-outline ki-paper-clip"></i>
                          Attachment {i + 1}
                        </a>
                      )
                    )}
                  </div>
                </div>
              )}

              <div className="d6m-section">
                <div className="d6m-section-head">
                  <span>Update status</span>
                  {updatingStatus && <span className="spinner-border spinner-border-sm text-gray-400"></span>}
                </div>
                <div className="d6m-seg">
                  {["Pending", "Completed", "Canceled"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={status === opt ? "active" : ""}
                      disabled={updatingStatus}
                      onClick={() => handleUpdateStatus(opt)}
                    >
                      <span className="d6m-dot" style={{ background: REQUEST_STATUS_DOT[opt] }} />
                      {opt}
                    </button>
                  ))}
                </div>
                <div className="d6m-hint">Residents see the status change in their app.</div>
              </div>
            </>
          )
        ) : (
          <>
            <div className="d6m-thread">
              {loading ? (
                <div className="d6m-empty">
                  <span className="spinner-border spinner-border-sm me-2 text-gray-400"></span>Loading messages…
                </div>
              ) : notes.length === 0 ? (
                <div className="d6m-empty">
                  <i className="ki-outline ki-messages fs-2x text-gray-300 d-block mb-2"></i>
                  No messages yet. Start the conversation below.
                </div>
              ) : (
                notes.map((note, i) => {
                  const isAdmin = ["admin", "estateadmin"].includes(String(note.senderType || "").toLowerCase());
                  const name = isAdmin ? note.senderName || "Admin" : note.senderName || residentName;
                  const isNewest = justSent && i === notes.length - 1;
                  return (
                    <div key={note.id || i} className={`d6m-msg ${isAdmin ? "mine" : ""} ${isNewest ? "d6-bubble-in" : ""}`}>
                      {!isAdmin && <Avatar src={note.senderAvatar || note.avatarUrl || avatarImg} name={name} size={28} />}
                      <div className="d6m-msg-body">
                        <div className="d6m-msg-meta">
                          <span>{name}</span>
                          <span>{fmtDateTime(note.createdDate)}</span>
                        </div>
                        <div className="d6m-bubble">{note.message}</div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={notesEndRef} />
            </div>
            <div className="d6m-composer">
              <textarea
                rows={2}
                placeholder="Write a reply to the resident…"
                value={replyText}
                onChange={(e) => {
                  const val = e.target.value;
                  setReplyText(val.length === 1 ? val.toUpperCase() : val);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSendReply();
                }}
              />
              <button
                type="button"
                className={`btn btn-success d6-mbtn flex-shrink-0 ${justSent ? "d6-mbtn-sent" : ""}`}
                onClick={handleSendReply}
                disabled={(!replyText.trim() && !justSent) || sendingReply}
              >
                {sendingReply ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : justSent ? (
                  <>
                    <svg viewBox="0 0 24 24" className="d6-mini-tick me-2" aria-hidden="true"><path d="M5 12.5 L10 17.5 L19 7.5" /></svg>
                    Sent
                  </>
                ) : (
                  "Send"
                )}
              </button>
            </div>
          </>
        )}
      </ModalShell>
      <FullScreenImageViewer
        isOpen={fullscreenViewer.isOpen}
        images={fullscreenViewer.images}
        initialIndex={fullscreenViewer.initialIndex}
        title={fullscreenViewer.title}
        onClose={() => setFullscreenViewer((prev) => ({ ...prev, isOpen: false }))}
      />
    </>
  );
}

const TITLE_OPTIONS = [
  "Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof.", "Engr.", "Arch.", "Barr.", "Pharm.", "Senator", "Honorable", "Chief",
  "General", "Colonel", "Major", "Captain", "Lieutenant", "Pastor", "Reverend", "Bishop", "Apostle", "Prophet",
  "Evangelist", "Alhaji", "Alhaja", "Hajia", "Mallam", "Sheikh", "Imam",
];

const BLANK_TENANT_FORM = {
  isCorporate: false,
  title: "", firstName: "", lastName: "", email: "", phoneNumber: "", occupation: "",
  corporateName: "", contactPersonFirstName: "", contactPersonLastName: "",
  contactPersonEmail: "", contactPersonPhoneNumber: "",
};

function Field({ label, required, hint, error, children, className = "" }) {
  return (
    <div className={`d6m-field ${className}`}>
      <label>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {error ? <div className="d6m-error">{error}</div> : hint ? <div className="d6m-hint">{hint}</div> : null}
    </div>
  );
}

function PhoneInput({ value, onChange, invalid }) {
  return (
    <div className={`d6m-input-group ${invalid ? "is-invalid" : ""}`}>
      <span>+234</span>
      <input
        type="tel"
        maxLength={10}
        placeholder="8012345678"
        value={value.replace(/^\+234|^0/, "")}
        onChange={(e) => {
          const digits = e.target.value.replace(/[^0-9]/g, "").replace(/^0+/, "");
          onChange(digits ? `+234${digits}` : "");
        }}
      />
    </div>
  );
}

function AddTenantModal({ modalRef, estateId, adminId, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);
  const [isCustomTitle, setIsCustomTitle] = useState(false);
  const [form, setForm] = useState(BLANK_TENANT_FORM);

  useEffect(() => {
    if (!form.isCorporate) {
      const fName = form.firstName.trim().toLowerCase().replace(/\s+/g, "");
      const lName = form.lastName.trim().toLowerCase().replace(/\s+/g, "");
      setForm((prev) => ({ ...prev, email: fName || lName ? `${fName}${lName}@zangon.com` : "" }));
    } else {
      const cpFName = form.contactPersonFirstName.trim().toLowerCase().replace(/\s+/g, "");
      const cpLName = form.contactPersonLastName.trim().toLowerCase().replace(/\s+/g, "");
      const corpName = form.corporateName.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
      let autoEmail = "";
      if (cpFName || cpLName) autoEmail = `${cpFName}${cpLName}@zangon.com`;
      else if (corpName) autoEmail = `${corpName}@zangon.com`;
      setForm((prev) => ({ ...prev, contactPersonEmail: autoEmail, email: autoEmail }));
    }
  }, [form.firstName, form.lastName, form.corporateName, form.contactPersonFirstName, form.contactPersonLastName, form.isCorporate]);

  const set = (key) => (e) => {
    const val = e.target.value;
    setForm((p) => ({ ...p, [key]: val.length === 1 ? val.toUpperCase() : val }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const e = {};
    if (form.isCorporate) {
      if (!form.corporateName.trim()) e.corporateName = "Enter the company name";
      if (!form.contactPersonEmail.trim() || !/\S+@\S+\.\S+/.test(form.contactPersonEmail)) e.contactPersonEmail = "Add a contact name to generate the email";
      if (!form.contactPersonPhoneNumber.trim()) e.contactPersonPhoneNumber = "Enter a phone number";
    } else {
      if (!form.firstName.trim()) e.firstName = "Enter a first name";
      if (!form.lastName.trim()) e.lastName = "Enter a last name";
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Add a name to generate the email";
      if (!form.phoneNumber.trim()) e.phoneNumber = "Enter a phone number";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const resetAndClose = () => {
    setErrors({}); setImagePreview(null); setImageFile(null);
    setIsCustomTitle(false);
    setForm(BLANK_TENANT_FORM);
    setSuccess(null);
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      let imageUrl = null;
      if (imageFile && adminId) {
        setUploadingImage(true);
        imageUrl = await uploadAdminImage(imageFile, adminId);
        setUploadingImage(false);
        if (!imageUrl) toast("Photo upload failed — tenant will be created without a photo.", { icon: "⚠️" });
      }

      const cpFirstName = form.contactPersonFirstName.trim() || "Contact";
      const cpLastName = form.contactPersonLastName.trim() || "Person";
      const contactPerson = {
        firstName: cpFirstName,
        lastName: cpLastName,
        email: form.contactPersonEmail.trim(),
        phoneNumber: form.contactPersonPhoneNumber.trim(),
        jobTitle: "",
      };

      const payload = form.isCorporate
        ? {
            name: form.corporateName.trim(),
            firstName: form.corporateName.trim(),
            lastName: "",
            email: form.contactPersonEmail.trim(),
            phoneNumber: form.contactPersonPhoneNumber.trim(),
            sector: form.occupation.trim() || "",
            isCorporateTenant: true,
            ...(imageUrl && { imageUrl }),
            contactPerson,
          }
        : {
            estateId,
            title: form.title.trim(),
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.email.trim(),
            phoneNumber: form.phoneNumber.trim(),
            occupation: form.occupation.trim(),
            isCorporateTenant: false,
            ...(imageUrl && { imageUrl }),
          };

      const endpoint = form.isCorporate ? "/tenants/corporate" : "/tenants/individual";
      const res = await authFetch(`${API_BASE}${endpoint}`, { method: "POST", body: JSON.stringify(payload) });
      const json = safeJsonParse(await res.text());

      if (!res.ok) {
        let msg = "Failed to create tenant.";
        if (res.status === 400 && json?.errors) {
          const k = Object.keys(json.errors)[0];
          msg = `${k}: ${json.errors[k][0]}`;
        } else if (json?.message) {
          msg = json.message;
        }
        throw new Error(msg);
      }

      const newTenantId = json?.data?.id || json?.data?.tenantId || json?.data;
      if (form.isCorporate && newTenantId) {
        const cpRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${newTenantId}/contact-persons`, {
          method: "POST",
          body: JSON.stringify(contactPerson),
        });
        if (!cpRes.ok) toast("Company tenant created, but linking the contact person failed.", { icon: "⚠️" });
      }

      const successName = form.isCorporate ? form.corporateName.trim() : `${form.firstName} ${form.lastName}`.trim();
      setSuccess({
        title: "Tenant added",
        message: `${successName} has been added to your estate.`,
      });
    } catch (err) {
      toast.error(err.message || "Failed to create tenant.");
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  const titleIsPreset = TITLE_OPTIONS.includes(form.title);
  const inputCls = (key) => `d6m-input ${errors[key] ? "is-invalid" : ""}`;
  const busy = loading || uploadingImage;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-profile-user"
      title="Add tenant"
      onClose={resetAndClose}
      closeDisabled={busy}
      overlay={
        success && (
          <SuccessState
            title={success.title}
            message={success.message}
            duration={2600}
            onDone={() => {
              resetAndClose();
              onSuccess?.();
            }}
          />
        )
      }
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={resetAndClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleSubmit} disabled={busy}>
            {uploadingImage ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Uploading photo…</>
            ) : loading ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Creating…</>
            ) : (
              "Create tenant"
            )}
          </button>
        </>
      }
    >
      <div className="d6m-seg mb-5">
        {[{ label: "Individual", icon: "ki-user", val: false }, { label: "Corporate", icon: "ki-office-bag", val: true }].map(({ label, icon, val }) => (
          <button
            key={label}
            type="button"
            className={form.isCorporate === val ? "active" : ""}
            onClick={() => { setForm((p) => ({ ...p, isCorporate: val })); setErrors({}); }}
          >
            <i className={`ki-outline ${icon}`}></i>
            {label}
          </button>
        ))}
      </div>

      <div className="d6m-upload">
        <button type="button" className="d6m-upload-preview" onClick={() => fileInputRef.current?.click()} aria-label="Upload photo">
          {imagePreview ? <img src={imagePreview} alt="" /> : <i className="ki-outline ki-picture"></i>}
        </button>
        <div className="flex-grow-1 min-w-0">
          <div className="d6m-v">{form.isCorporate ? "Company logo" : "Profile photo"}</div>
          <div className="d6m-muted">Optional · PNG or JPG, up to 5MB</div>
        </div>
        <div className="d-flex gap-2">
          {imagePreview && (
            <button type="button" className="d6m-link-btn" onClick={() => { setImageFile(null); setImagePreview(null); }}>Remove</button>
          )}
          <button type="button" className="btn btn-light d6-mbtn d6-mbtn-sm" onClick={() => fileInputRef.current?.click()}>
            {imagePreview ? "Change" : "Upload"}
          </button>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleImageSelect} />
      </div>

      {form.isCorporate ? (
        <div className="d6m-section">
          <div className="d6m-section-head"><span>Company</span></div>
          <div className="row g-4">
            <Field className="col-12" label="Company name" required error={errors.corporateName}>
              <input className={inputCls("corporateName")} placeholder="Acme Ltd" value={form.corporateName} onChange={set("corporateName")} />
            </Field>
          </div>
          <div className="d6m-section-head mt-6"><span>Contact person</span></div>
          <div className="row g-4">
            <Field className="col-6" label="First name">
              <input className={inputCls("contactPersonFirstName")} placeholder="Jane" value={form.contactPersonFirstName} onChange={set("contactPersonFirstName")} />
            </Field>
            <Field className="col-6" label="Last name">
              <input className={inputCls("contactPersonLastName")} placeholder="Doe" value={form.contactPersonLastName} onChange={set("contactPersonLastName")} />
            </Field>
            <Field className="col-12" label="Phone number" required error={errors.contactPersonPhoneNumber}>
              <PhoneInput
                value={form.contactPersonPhoneNumber}
                invalid={!!errors.contactPersonPhoneNumber}
                onChange={(v) => setForm((p) => ({ ...p, contactPersonPhoneNumber: v }))}
              />
            </Field>
            <Field className="col-12" label="Login email" hint="Generated from the contact's name." error={errors.contactPersonEmail}>
              <input className={`${inputCls("contactPersonEmail")} is-readonly`} placeholder="janedoe@zangon.com" value={form.contactPersonEmail} readOnly />
            </Field>
            <Field className="col-12" label="Sector or notes">
              <textarea className="d6m-input" rows="2" placeholder="e.g. Oil & gas, staff housing" value={form.occupation} onChange={(e) => setForm((p) => ({ ...p, occupation: e.target.value }))}></textarea>
            </Field>
          </div>
        </div>
      ) : (
        <div className="d6m-section">
          <div className="d6m-section-head"><span>Personal details</span></div>
          <div className="row g-4">
            <Field className="col-4" label="Title">
              <select
                className="d6m-input"
                value={isCustomTitle ? "OTHERS" : titleIsPreset ? form.title : form.title ? "OTHERS" : ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "OTHERS") setIsCustomTitle(true);
                  else { setIsCustomTitle(false); setForm((p) => ({ ...p, title: val })); }
                }}
              >
                <option value="">None</option>
                {TITLE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                <option value="OTHERS">Other…</option>
              </select>
            </Field>
            <Field className="col-8" label="First name" required error={errors.firstName}>
              <input className={inputCls("firstName")} placeholder="John" value={form.firstName} onChange={set("firstName")} />
            </Field>
            {(isCustomTitle || (form.title && !titleIsPreset)) && (
              <Field className="col-12" label="Custom title">
                <input className="d6m-input" placeholder="e.g. Justice" value={titleIsPreset ? "" : form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
              </Field>
            )}
            <Field className="col-12" label="Last name" required error={errors.lastName}>
              <input className={inputCls("lastName")} placeholder="Doe" value={form.lastName} onChange={set("lastName")} />
            </Field>
            <Field className="col-12" label="Phone number" required error={errors.phoneNumber}>
              <PhoneInput value={form.phoneNumber} invalid={!!errors.phoneNumber} onChange={(v) => setForm((p) => ({ ...p, phoneNumber: v }))} />
            </Field>
            <Field className="col-12" label="Login email" hint="Generated from their name." error={errors.email}>
              <input className={`${inputCls("email")} is-readonly`} placeholder="johndoe@zangon.com" value={form.email} readOnly />
            </Field>
            <Field className="col-12" label="Occupation">
              <input className="d6m-input" placeholder="e.g. Software engineer" value={form.occupation} onChange={(e) => setForm((p) => ({ ...p, occupation: e.target.value }))} />
            </Field>
          </div>
        </div>
      )}

    </ModalShell>
  );
}

const CategorySkeleton = () => (
  <div className="d6m-list">
    {[1, 2, 3, 4].map((n) => (
      <div key={n} className="d6m-list-row">
        <Shimmer w={40} h={40} r={10} />
        <div className="flex-grow-1 d-flex flex-column gap-2">
          <Shimmer w="40%" h={12} />
          <Shimmer w="65%" h={10} />
        </div>
      </div>
    ))}
  </div>
);

function ManageCategoriesModal({ modalRef, estateId }) {
  const { hide } = useBootstrapModal(modalRef);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ id: "", name: "", description: "" });

  const fetchCategories = useCallback(async ({ silent = false } = {}) => {
    if (!estateId) return;
    if (!silent) setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/maintenance-request-categories`);
      const json = res.ok ? safeJsonParse(await res.text()) : null;
      setCategories(parseArrayData(json));
    } catch {
      toast.error("Failed to load categories");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [estateId]);

  const fetchedEstateId = useRef(null);
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return undefined;
    const onShow = () => {
      if (fetchedEstateId.current === estateId) return;
      fetchedEstateId.current = estateId;
      fetchCategories();
    };
    el.addEventListener("show.bs.modal", onShow);
    return () => el.removeEventListener("show.bs.modal", onShow);
  }, [estateId, modalRef, fetchCategories]);

  const clearForm = () => setForm({ id: "", name: "", description: "" });
  const resetClose = () => { hide(); setSuccess(null); clearForm(); };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Category name is required.");
    setSaving(true);
    try {
      const isEdit = !!form.id;
      const url = isEdit
        ? `${API_BASE}/estates/${estateId}/maintenance-request-categories/${form.id}`
        : `${API_BASE}/estates/${estateId}/maintenance-request-categories`;
      const res = await authFetch(url, {
        method: isEdit ? "PUT" : "POST",
        body: JSON.stringify({ name: form.name, description: form.description }),
      });
      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to save category.");
      const savedName = form.name;
      clearForm();
      await fetchCategories({ silent: true });
      setSuccess({
        title: isEdit ? "Category updated" : "Category created",
        message: `${savedName} is ${isEdit ? "updated" : "now available"} for residents logging maintenance requests.`,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-category"
      title="Maintenance categories"
      onClose={resetClose}
      overlay={success && <SuccessState title={success.title} message={success.message} duration={2000} onDone={() => setSuccess(null)} />}
      footer={<button type="button" className="btn btn-light d6-mbtn" onClick={resetClose}>Close</button>}
    >
      <div className="d6m-card">
        <div className="d6m-section-head mb-4">
          <span>{form.id ? `Edit ${form.name}` : "Add a category"}</span>
        </div>
        <div className="d-flex flex-column gap-4">
          <Field label="Category">
            <CategoryDropdown
              value={form.name}
              onChange={(val) => {
                const cat = MAINTENANCE_CATEGORIES.find((c) => c.value === val);
                setForm((p) => ({ ...p, name: val, description: cat?.description || "" }));
              }}
              placeholder="Choose a category…"
            />
          </Field>
          <Field label="Description" hint="Shown to residents when they pick this category.">
            <textarea
              className="d6m-input"
              rows="2"
              placeholder="Brief description…"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </Field>
          <div className="d-flex gap-2 justify-content-end">
            {form.id && <button type="button" className="btn btn-light d6-mbtn" onClick={clearForm}>Cancel edit</button>}
            <button type="button" className="btn btn-success d6-mbtn" onClick={handleSave} disabled={saving || !form.name.trim()}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : form.id ? "Save changes" : "Add category"}
            </button>
          </div>
        </div>
      </div>

      <div className="d6m-section">
        <div className="d6m-section-head">
          <span>Existing categories</span>
          <span className="d6m-muted">Select one to edit</span>
        </div>
        {loading ? (
          <CategorySkeleton />
        ) : categories.length === 0 ? (
          <div className="d6m-empty">No categories yet. Add your first one above.</div>
        ) : (
          <div className="d6m-list">
            {categories.map((c) => {
              const isEditing = form.id === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`d6m-list-row ${isEditing ? "active" : ""}`}
                  onClick={() => setForm({ id: c.id, name: c.name, description: c.description || "" })}
                >
                  <span className="d6m-tile"><i className={`ki-outline ${c.icon || getCategoryIconMeta(c.name).icon}`}></i></span>
                  <span className="min-w-0 flex-grow-1 text-start">
                    <span className="d6m-v d-block text-truncate">{c.name}</span>
                    <span className="d6m-muted d-block text-truncate">{c.description || "No description"}</span>
                  </span>
                  <span className="d6m-edit">{isEditing ? "Editing" : "Edit"}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </ModalShell>
  );
}

function toTitleCase(str) {
  if (!str) return "";
  return String(str).replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

const EXPORT_COLUMNS = [
  { key: "title", label: "Title", hint: "Mr., Mrs., Dr. …" },
  { key: "tenantName", label: "Tenant name" },
  { key: "email", label: "Email address" },
  { key: "phoneNumber", label: "Phone number" },
  { key: "rentStatus", label: "Rent status" },
  { key: "currentBuildings", label: "Units", hint: "Building numbers" },
  { key: "phase", label: "Phase" },
  { key: "dependents", label: "Dependents", hint: "Names and relationship" },
];

function DownloadTenantModal({ modalRef, tenants = [] }) {
  const [options, setOptions] = useState(() => Object.fromEntries(EXPORT_COLUMNS.map((c) => [c.key, true])));
  const [downloading, setDownloading] = useState(false);
  const [success, setSuccess] = useState(null);

  const rows = useMemo(
    () =>
      tenants.map((t) => ({
        title: t.title || "",
        name: t.name || `${t.firstName || ""} ${t.lastName || ""}`.trim(),
        email: t.email || "",
        phone: t.phone || t.phoneNumber || "",
        rentStatus: t.rentStatus || "",
        buildings: t.buildings || t.currentBuildings || [],
        dependents: t.dependents || [],
        isCorp: !!(t.isCorp ?? t.isCorporateTenant),
      })),
    [tenants]
  );

  const selectedCount = Object.values(options).filter(Boolean).length;
  const allSelected = selectedCount === EXPORT_COLUMNS.length;

  const close = () => {
    setSuccess(null);
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };

  const handleDownload = () => {
    const activeKeys = EXPORT_COLUMNS.filter((c) => options[c.key]);
    if (activeKeys.length === 0) { toast.error("Select at least one column to export."); return; }
    if (rows.length === 0) { toast.error("There are no tenants to export yet."); return; }

    setDownloading(true);
    try {
      const buildRow = (item) => {
        const row = {};
        activeKeys.forEach(({ key, label }) => {
          switch (key) {
            case "title": row[label] = item.isCorp ? "" : toTitleCase(item.title); break;
            case "tenantName": row[label] = toTitleCase(item.name); break;
            case "email": row[label] = item.email; break;
            case "phoneNumber": row[label] = item.phone; break;
            case "rentStatus": row[label] = item.rentStatus; break;
            case "currentBuildings": row[label] = item.buildings.map((b) => b.number).filter(Boolean).join(", "); break;
            case "phase": row[label] = [...new Set(item.buildings.map((b) => b.phaseName).filter(Boolean))].join(", "); break;
            case "dependents":
              row[label] = item.dependents
                .map((dep) => `${toTitleCase(dep.firstName || "")} ${toTitleCase(dep.lastName || "")} (${dep.relationship || "Dependent"})`.trim())
                .join(", ") || "None";
              break;
            default: row[label] = "";
          }
        });
        return row;
      };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.filter((r) => !r.isCorp).map(buildRow)), "Individual Tenants");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.filter((r) => r.isCorp).map(buildRow)), "Corporate Tenants");
      XLSX.writeFile(wb, `tenants_log_${new Date().toISOString().split("T")[0]}.xlsx`);
      setSuccess({
        title: "Download ready",
        message: `${rows.length} tenant${rows.length === 1 ? "" : "s"} exported to Excel with ${activeKeys.length} column${activeKeys.length === 1 ? "" : "s"}.`,
      });
    } catch (err) {
      toast.error("Failed to generate download.");
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-file-down"
      title="Download tenant log"
      onClose={close}
      closeDisabled={downloading}
      width={640}
      overlay={success && <SuccessState title={success.title} message={success.message} duration={2000} onDone={close} />}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={close} disabled={downloading}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleDownload} disabled={downloading || selectedCount === 0}>
            {downloading ? <><span className="spinner-border spinner-border-sm me-2"></span>Exporting…</> : "Download"}
          </button>
        </>
      }
    >
      <div className="d6m-note mt-0 mb-5">
        <i className="ki-outline ki-information-5"></i>
        <span>Individual and corporate tenants are saved on separate sheets in the same file.</span>
      </div>
      <div className="d6m-section-head">
        <span>Columns to include</span>
        <button
          type="button"
          className="d6m-link-btn"
          onClick={() => setOptions(Object.fromEntries(EXPORT_COLUMNS.map((c) => [c.key, !allSelected])))}
        >
          {allSelected ? "Clear all" : "Select all"}
        </button>
      </div>
      <div className="d6m-list">
        {EXPORT_COLUMNS.map((c) => (
          <label key={c.key} className={`d6m-list-row d6m-check-row ${options[c.key] ? "active" : ""}`}>
            <input
              type="checkbox"
              checked={options[c.key]}
              onChange={(e) => setOptions((o) => ({ ...o, [c.key]: e.target.checked }))}
              disabled={downloading}
            />
            <span className="d6m-check" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M5 12.5 L10 17.5 L19 7.5" /></svg>
            </span>
            <span className="min-w-0 flex-grow-1">
              <span className="d6m-v d-block">{c.label}</span>
              {c.hint && <span className="d6m-muted d-block">{c.hint}</span>}
            </span>
          </label>
        ))}
      </div>
    </ModalShell>
  );
}


// Categorical order validated for CVD separation + contrast; assign in this order, never cycle.
const SERIES = ["#0B9446", "#2563EB", "#D97706", "#9333EA", "#0891B2"];
const CLOCK_OUT = "#DC2626";

// Same occupancy rule as Rent Management: a unit counts as occupied when the building record says so,
// or when any tenant's current buildings match it by id or unit number.
function tenantBuildingsOf(t) {
  return t.currentBuildings || t.assignedBuildings || (t.assignedBuilding ? [t.assignedBuilding] : []);
}

function checkIsBuildingAssigned(building, tenants = []) {
  if (!building) return false;
  if (
    building.isAssigned ||
    building.isOccupied ||
    building.status === "Occupied" ||
    building.status === "Assigned" ||
    building.currentTenantId ||
    building.currentTenantName ||
    building.tenantId ||
    building.tenantName ||
    building.occupantName
  ) {
    return true;
  }
  const bId = building.id || building.buildingId;
  const bNum = String(building.number || building.buildingNumber || "").trim();
  return tenants.some((t) =>
    tenantBuildingsOf(t).some((tb) => {
      const tbId = tb.id || tb.buildingId;
      const tbNum = String(tb.number || tb.buildingNumber || "").trim();
      return (tbId && bId && String(tbId) === String(bId)) || (tbNum && bNum && tbNum === bNum);
    })
  );
}

const GAUGE_NEUTRAL = "#D4D4D8";

function gaugeArc(cx, cy, R, r, a0, a1) {
  const pt = (rad, deg) => [cx + rad * Math.cos((deg * Math.PI) / 180), cy - rad * Math.sin((deg * Math.PI) / 180)];
  const [x0, y0] = pt(R, a0);
  const [x1, y1] = pt(R, a1);
  const [x2, y2] = pt(r, a1);
  const [x3, y3] = pt(r, a0);
  return `M ${x0} ${y0} A ${R} ${R} 0 0 1 ${x1} ${y1} L ${x2} ${y2} A ${r} ${r} 0 0 0 ${x3} ${y3} Z`;
}

const GAUGE_TICKS = Array.from({ length: 21 }, (_, k) => 180 - k * 9);

function SectionGauge({ segments, center }) {
  const uid = useId().replace(/:/g, "");
  const [hover, setHover] = useState(null);
  const total = segments.reduce((a, sg) => a + (sg.value || 0), 0);
  const drawn = segments.map((sg, i) => ({ ...sg, i })).filter((sg) => sg.value > 0);
  const GAP = 2.4;
  const span = 180 - GAP * Math.max(0, drawn.length - 1);
  let cursor = 180;
  const arcs = drawn.map((sg) => {
    const sweep = (sg.value / total) * span;
    const d = gaugeArc(100, 104, 92, 62, cursor, cursor - sweep);
    cursor -= sweep + GAP;
    return { ...sg, d };
  });
  const lead = arcs.reduce((best, a) => (!best || a.value > best.value ? a : best), null);
  const active = hover != null ? segments[hover] : null;
  const pct = (v) => (total ? Math.round((v / total) * 100) : 0);

  return (
    <div>
      <div className="position-relative mx-auto" style={{ maxWidth: 320 }}>
        <svg viewBox="0 0 200 112" className="w-100 d-block" style={{ overflow: "visible" }}>
          <defs>
            <pattern id={`${uid}-hatch`} width="5" height="5" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
              <line x1="0" y1="0" x2="0" y2="5" stroke="#ffffff" strokeWidth="1.8" strokeOpacity="0.45" />
            </pattern>
          </defs>
          {GAUGE_TICKS.map((t) => {
            const major = t % 45 === 0;
            const rad = (t * Math.PI) / 180;
            const r2 = major ? 103 : 100.5;
            return (
              <line
                key={t}
                x1={100 + 97 * Math.cos(rad)}
                y1={104 - 97 * Math.sin(rad)}
                x2={100 + r2 * Math.cos(rad)}
                y2={104 - r2 * Math.sin(rad)}
                stroke={major ? "#c4c4cc" : "#e4e4e7"}
                strokeWidth={major ? 1.4 : 1}
                strokeLinecap="round"
              />
            );
          })}
          {total === 0 ? (
            <path d={gaugeArc(100, 104, 92, 62, 180, 0)} fill="#f1f1f3" />
          ) : (
            arcs.map((a, k) => (
              <g
                key={a.label}
                className="d6-gauge-seg"
                style={{ animationDelay: `${k * 90}ms`, opacity: hover == null || hover === a.i ? 1 : 0.3 }}
                onMouseEnter={() => setHover(a.i)}
                onMouseLeave={() => setHover(null)}
              >
                <path d={a.d} fill={a.color} />
                {lead && lead.i === a.i && <path d={a.d} fill={`url(#${uid}-hatch)`} pointerEvents="none" />}
                <title>{`${a.label}: ${a.value.toLocaleString()} (${pct(a.value)}%)`}</title>
              </g>
            ))
          )}
        </svg>
        <div className="position-absolute start-0 end-0 bottom-0 d-flex flex-column align-items-center text-center" style={{ pointerEvents: "none" }}>
          {total === 0 ? (
            <span className="fs-8 text-gray-400 mb-4">No data yet</span>
          ) : active ? (
            <>
              <span className="text-gray-900 d6-num">
                {active.value.toLocaleString()}
              </span>
              <span className="text-gray-400 fs-8 mt-1">{active.label} · {pct(active.value)}%</span>
            </>
          ) : (
            center()
          )}
        </div>
      </div>

      <div className="d6-gauge-legend" style={{ gridTemplateColumns: `repeat(${Math.min(3, Math.max(1, segments.length))}, minmax(0, 1fr))` }}>
        {segments.map((sg, i) => (
          <button
            key={sg.label}
            type="button"
            className={`d6-gauge-key ${hover === i ? "on" : ""}`}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          >
            <span className="legend-dot mb-1" style={{ background: sg.color }} />
            <span className="fs-8 text-gray-700 text-truncate mw-100">{sg.label}</span>
            <span className="fs-9 text-gray-400">{(sg.value || 0).toLocaleString()} · {pct(sg.value || 0)}%</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SegTabs({ options, value, onChange }) {
  return (
    <div className="d6-seg">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`fs-8 fw-bold ${value === o.value ? "active" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function CardHead({ title, subtitle, children, className = "mb-4" }) {
  return (
    <div className={`d-flex align-items-start justify-content-between flex-wrap gap-3 ${className}`}>
      <div className="min-w-0">
        <h3 className="text-gray-900 d6-title mb-1">{title}</h3>
        {subtitle && <span className="text-gray-400 fs-8">{subtitle}</span>}
      </div>
      {children && <div className="d-flex align-items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="d6-tooltip">
      <div className="fs-9 fw-semibold text-gray-400 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="d-flex align-items-center gap-2 fs-8">
          <span className="legend-dot" style={{ background: p.color }} />
          <span className="text-gray-600">{p.name}</span>
          <span className="fw-bold text-gray-900 ms-auto ps-3">{Number(p.value || 0).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard5() {
  const navigate = useNavigate();
  const session = getSession();
  const estateId = session?.estateId || "";
  const adminFirstName = session?.firstName || "Admin";
  const adminLastName = session?.lastName || "";
  const adminFullName = `${adminFirstName} ${adminLastName}`.trim();

  const greeting = useMemo(() => getGreeting(), []);

  const [activeHouseIndex, setActiveHouseIndex] = useState(0);
  const [houseAnimKey, setHouseAnimKey] = useState(0);
  const [allEstateTenants, setAllEstateTenants] = useState([]);
  const [estateBuildingTypes, setEstateBuildingTypes] = useState([]);
  const [estateBuildings, setEstateBuildings] = useState([]);
  const [estatePhases, setEstatePhases] = useState([]);
  // GET /dashboard/properties: { totalUnits, occupiedUnits, vacantUnits, occupancyPercentage, phases[] }.
  const [propertiesSummary, setPropertiesSummary] = useState(null);
  const [rawAccessLogs, setRawAccessLogs] = useState([]);
  const [insideCount, setInsideCount] = useState(0);
  const [trendPeriod, setTrendPeriod] = useState("Today");
  const [showClockIn, setShowClockIn] = useState(true);
  const [showClockOut, setShowClockOut] = useState(true);

  const [stats, setStats] = useState({
    propertiesManaged: 0,
    totalTenants: 0,
    totalRentCollected: 0,
    pendingRent: 0,
    owingTenantsCount: 0,
    visitorsToday: 0,
    passesIssued: 0,
    activeVisits: 0,
  });

  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [occupancyTab, setOccupancyTab] = useState("current"); // "current" | "phase"


  // Building Type Library synced with global configurations & assignments
  const allHouseTypes = useMemo(() => {
    if (estateBuildingTypes && estateBuildingTypes.length > 0) {
      return estateBuildingTypes.map((bt, idx) => {
        const typeBuildings = (estateBuildings || []).filter(
          (b) =>
            String(b.typeId || b.buildingTypeId || "") === String(bt.id || "") ||
            (b.typeName && b.typeName.trim().toLowerCase() === bt.name?.trim().toLowerCase())
        );
        const usageCount = typeBuildings.length;
        const occupiedCount = typeBuildings.filter((b) => checkIsBuildingAssigned(b, allEstateTenants)).length;

        const phases = [...new Set(typeBuildings.map((b) => b.phaseName).filter(Boolean))];
        const phaseStr = phases.length > 0 ? phases.join(", ") : "Phase 1";
        const subtitle = `${phaseStr} • ${usageCount} ${usageCount === 1 ? "Unit" : "Units"}`;

        return {
          id: bt.id || `bt-${idx}`,
          name: bt.name,
          typeName: bt.name,
          phaseName: phaseStr,
          subtitle,
          image: bt.imageUrl || bt.imagePreview || getHouseImage(bt.name),
          totalUnits: usageCount,
          occupiedUnits: occupiedCount,
          rawType: bt,
          avatarIndices: [
            ((idx * 6) % 30) + 1,
            ((idx * 6 + 1) % 30) + 1,
            ((idx * 6 + 2) % 30) + 1,
            ((idx * 6 + 3) % 30) + 1,
            ((idx * 6 + 4) % 30) + 1,
            ((idx * 6 + 5) % 30) + 1,
          ],
        };
      });
    }

    return [];
  }, [estateBuildingTypes, estateBuildings, allEstateTenants]);

  // 12-second auto-rotation of house types
  useEffect(() => {
    const listLen = allHouseTypes.length || 1;
    const interval = setInterval(() => {
      setActiveHouseIndex((curr) => (curr + 1) % listLen);
      setHouseAnimKey((k) => k + 1);
    }, 12000);
    return () => clearInterval(interval);
  }, [allHouseTypes.length]);

  const currentHouse = allHouseTypes[activeHouseIndex % allHouseTypes.length] || allHouseTypes[0];

  // Strictly resolve residents living in the CURRENT building type only
  const currentResidents = useMemo(() => {
    if (!currentHouse) return { avatars: [], othersCount: 0, totalCount: 0 };

    const norm = (s) =>
      (s || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "")
        .replace(/bedrooms?/g, "bedroom");

    const targetTypeId = String(currentHouse.id || "");
    const targetNormName = norm(currentHouse.name || currentHouse.typeName);

    // Map building type IDs to names
    const typeNameById = new Map();
    (estateBuildingTypes || []).forEach((bt) => {
      if (bt.id) typeNameById.set(String(bt.id), bt.name);
    });

    // 1. Identify which buildings in estateBuildings belong to this building type
    const targetBuildingIds = new Set();
    const targetBuildingKeys = new Set(); // "phase___number"

    (estateBuildings || []).forEach((b) => {
      const bTypeId = String(b.typeId || b.buildingTypeId || "");
      const bTypeName = b.typeName || typeNameById.get(bTypeId) || "";
      const isMatch =
        (targetTypeId && bTypeId === targetTypeId) ||
        (targetNormName && norm(bTypeName) === targetNormName) ||
        (targetNormName && norm(bTypeName).length > 3 && (norm(bTypeName).includes(targetNormName) || targetNormName.includes(norm(bTypeName))));

      if (isMatch) {
        if (b.id) targetBuildingIds.add(String(b.id));
        const num = String(b.number || b.buildingNumber || "").trim().toLowerCase();
        const phase = String(b.phaseName || "").trim().toLowerCase();
        if (num && phase) {
          targetBuildingKeys.add(`${phase}___${num}`);
        }
      }
    });

    // 2. Filter tenants from /tenants/with-dependents living in this building type ONLY (no dependents)
    const tenantsLivingInThisType = (allEstateTenants || []).filter((t) => {
      const bList = [
        ...(Array.isArray(t.currentBuildings) ? t.currentBuildings : []),
        ...(Array.isArray(t.assignedBuildings) ? t.assignedBuildings : []),
        ...(t.assignedBuilding ? [t.assignedBuilding] : []),
        ...(t.building ? [t.building] : []),
      ];

      // If tenant has direct building info
      if (bList.length === 0 && (t.buildingNumber || t.number)) {
        bList.push({
          number: t.buildingNumber || t.number,
          phaseName: t.phaseName,
          id: t.buildingId,
          typeName: t.buildingType || t.typeName,
        });
      }

      const hasBuildingMatch = bList.some((b) => {
        // Match by building ID
        const bId = String(b.id || b.buildingId || "");
        if (bId && targetBuildingIds.has(bId)) return true;

        // Match by Phase + Number (exact match with estateBuildings)
        const num = String(b.number || b.buildingNumber || "").trim().toLowerCase();
        const phase = String(b.phaseName || "").trim().toLowerCase();
        if (num && phase && targetBuildingKeys.has(`${phase}___${num}`)) return true;

        // Match if building object itself has typeId or typeName
        const bTypeId = String(b.typeId || b.buildingTypeId || "");
        if (targetTypeId && bTypeId === targetTypeId) return true;

        const bTypeName = b.typeName || b.buildingTypeName || b.name || "";
        if (targetNormName && norm(bTypeName) === targetNormName) return true;

        // Match if currentHouse has matching buildingNumber & phaseName
        if (currentHouse.buildingNumber && currentHouse.phaseName) {
          if (
            num === String(currentHouse.buildingNumber).trim().toLowerCase() &&
            phase === String(currentHouse.phaseName).trim().toLowerCase()
          ) {
            return true;
          }
        }

        return false;
      });

      if (hasBuildingMatch) return true;

      // Match if tenant is assigned to any building of this type in estateBuildings
      const tid = String(t.tenantId || t.id || "");
      if (tid && (estateBuildings || []).some((eb) => targetBuildingIds.has(String(eb.id)) && String(eb.tenantId) === tid)) {
        return true;
      }

      // Match by root level building type name on tenant
      const tTypeName = t.buildingType || t.typeName || "";
      if (targetNormName && norm(tTypeName) === targetNormName) return true;

      return false;
    });

    const extractImage = (t) =>
      t.imageUrl ||
      t.image ||
      t.photoUrl ||
      t.avatarUrl ||
      t.profilePictureUrl ||
      t.profileImage ||
      t.avatar ||
      t.tenantProfilePictureUrl ||
      t.tenantImageUrl ||
      null;

    const isValidImage = (img) =>
      !!img &&
      typeof img === "string" &&
      img.trim().length > 0 &&
      !img.toLowerCase().includes("blank") &&
      !img.toLowerCase().includes("default") &&
      !img.toLowerCase().includes("placeholder");

    // Collect ONLY tenants (not dependents) with real images from /tenants/with-dependents
    const tenantsWithImages = [];
    const seenTenantIds = new Set();

    tenantsLivingInThisType.forEach((t, tIdx) => {
      const tid = String(t.tenantId || t.id || `t-${tIdx}`);
      if (!seenTenantIds.has(tid)) {
        seenTenantIds.add(tid);
        const img = extractImage(t);
        if (isValidImage(img)) {
          let name = "";
          if (t.isCorporateTenant) {
            name = (t.name || `${t.firstName || ""} ${t.lastName || ""}`.trim() || "Corporate Tenant").trim();
          } else {
            const titleStr = t.title ? `${t.title} ` : "";
            name = (t.name || `${titleStr}${t.firstName || ""} ${t.lastName || ""}`.trim() || `Tenant ${tIdx + 1}`).trim();
          }

          const b0 = t.currentBuildings?.[0] || t.assignedBuildings?.[0] || {};
          const unitStr = b0.number ? (b0.phaseName ? `${b0.phaseName} • Unit ${b0.number}` : `Unit ${b0.number}`) : "";
          const displayName = unitStr ? `${name} (${unitStr})` : name;

          tenantsWithImages.push({
            id: tid,
            name: displayName,
            imageUrl: img,
          });
        }
      }
    });

    // Show at most 5 tenant images (no mock/fallback images)
    const avatars = tenantsWithImages.slice(0, 5);

    // Total tenants in this house type and others count
    const totalTenantsCount = Math.max(
      tenantsLivingInThisType.length,
      currentHouse.occupiedUnits || 0,
      tenantsWithImages.length
    );
    const othersCount = Math.max(0, totalTenantsCount - avatars.length);

    return {
      avatars,
      othersCount,
      totalCount: totalTenantsCount,
    };
  }, [currentHouse, estateBuildings, estateBuildingTypes, allEstateTenants]);

  const [pageReady, setPageReady] = useState(false);
  const today = useMemo(() => new Date(), []);
  const carouselRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [weatherData, setWeatherData] = useState(null);

  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");
    document.documentElement.setAttribute("data-bs-theme", localStorage.getItem("kt_theme_mode") || "light");

    const init = () => {
      window.KTThemeMode?.init?.(); window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.(); window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.(); window.KTScrollTop?.createInstances?.();
    };
    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!pageReady) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(".gsap-header", { opacity: 0, y: -16 }, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out", clearProps: "all" });
      gsap.fromTo(".gsap-hero", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", delay: 0.08, clearProps: "all" });
      gsap.fromTo(".stat-subcard", { opacity: 0, y: 14 }, { opacity: 1, y: 0, stagger: 0.06, duration: 0.5, ease: "back.out(1.2)", delay: 0.18, clearProps: "all" });
      gsap.fromTo(".gsap-card", { opacity: 0, y: 20 }, { opacity: 1, y: 0, stagger: 0.08, duration: 0.55, ease: "power2.out", delay: 0.25, clearProps: "all" });
      gsap.fromTo(".gsap-insights", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out", delay: 0.35, clearProps: "all" });
    });
    return () => ctx.revert();
  }, [pageReady]);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await fetch(
          "https://api.open-meteo.com/v1/forecast?latitude=9.0765&longitude=7.4426&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,apparent_temperature&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=Africa%2FLagos"
        );
        if (res.ok) setWeatherData(await res.json());
      } catch (e) {
        console.error("Open-Meteo weather fetch error:", e);
      }
    };
    fetchWeather();
  }, []);

  const parsed5DayForecast = useMemo(() => {
    if (!weatherData?.daily) {
      return [
        { day: "Today", maxTemp: 28, minTemp: 21, label: "Partly Cloudy", emoji: "⛅" },
        { day: "Thu", maxTemp: 29, minTemp: 22, label: "Mainly Clear", emoji: "☀️" },
        { day: "Fri", maxTemp: 27, minTemp: 20, label: "Rainy", emoji: "🌧️" },
        { day: "Sat", maxTemp: 30, minTemp: 21, label: "Sunny", emoji: "☀️" },
        { day: "Sun", maxTemp: 28, minTemp: 21, label: "Partly Cloudy", emoji: "⛅" },
      ];
    }
    const times = weatherData.daily.time || [];
    const maxTemps = weatherData.daily.temperature_2m_max || [];
    const minTemps = weatherData.daily.temperature_2m_min || [];
    const codes = weatherData.daily.weather_code || [];

    const result = [];
    for (let i = 0; i < Math.min(5, times.length); i++) {
      const dayLabel = i === 0 ? "Today" : new Date(times[i] + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" });
      const meta = getWeatherDescription(codes[i]);
      result.push({
        day: dayLabel,
        maxTemp: Math.round(maxTemps[i]),
        minTemp: Math.round(minTemps[i]),
        label: meta.text,
        emoji: meta.emoji,
      });
    }
    return result;
  }, [weatherData]);

  const rentCollectedPct = useMemo(() => {
    const total = stats.totalRentCollected + stats.pendingRent;
    if (total <= 0) return 0;
    return Math.round((stats.totalRentCollected / total) * 100);
  }, [stats.totalRentCollected, stats.pendingRent]);

  const handleMouseEnter = () => {
    if (carouselRef.current && window.bootstrap && window.bootstrap.Carousel) {
      window.bootstrap.Carousel.getOrCreateInstance(carouselRef.current).pause();
    }
  };

  const handleMouseLeave = () => {
    if (carouselRef.current && window.bootstrap && window.bootstrap.Carousel) {
      window.bootstrap.Carousel.getOrCreateInstance(carouselRef.current).cycle();
    }
  };

  const [pendingRentTenants, setPendingRentTenants] = useState([]);
  const [notActivatedTenants, setNotActivatedTenants] = useState([]);
  const [unassignedTenants, setUnassignedTenants] = useState([]);
  const [requests, setRequests] = useState([]);

  const accessTrendData = useMemo(() => {
    const now = new Date();

    if (trendPeriod === "Today") {
      const hours = Array.from({ length: 24 }).map((_, h) => {
        let label = "";
        if (h === 0) label = "12:00 AM";
        else if (h < 12) label = `${h}:00 AM`;
        else if (h === 12) label = "12:00 PM";
        else label = `${h - 12}:00 PM`;
        return {
          name: label,
          hour: h,
          clockIn: 0,
          clockOut: 0,
        };
      });

      (rawAccessLogs || []).forEach((l) => {
        if (l.checkedInAt) {
          const d = new Date(l.checkedInAt);
          if (
            d.getFullYear() === now.getFullYear() &&
            d.getMonth() === now.getMonth() &&
            d.getDate() === now.getDate()
          ) {
            const h = d.getHours();
            if (hours[h]) {
              hours[h].clockIn += 1;
            }
          }
        }
        if (l.checkedOutAt) {
          const d = new Date(l.checkedOutAt);
          if (
            d.getFullYear() === now.getFullYear() &&
            d.getMonth() === now.getMonth() &&
            d.getDate() === now.getDate()
          ) {
            const h = d.getHours();
            if (hours[h]) {
              hours[h].clockOut += 1;
            }
          }
        }
      });

      return hours;
    }

    if (trendPeriod === "Last 7 Days") {
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const dayName = d.toLocaleDateString("en-US", { weekday: "short" });
        days.push({
          name: dayName,
          fullName: d.toLocaleDateString("en-US", { weekday: "long" }),
          dObj: d,
          clockIn: 0,
          clockOut: 0,
        });
      }

      (rawAccessLogs || []).forEach((l) => {
        if (l.checkedInAt) {
          const d = new Date(l.checkedInAt);
          const match = days.find(
            (day) =>
              d.getFullYear() === day.dObj.getFullYear() &&
              d.getMonth() === day.dObj.getMonth() &&
              d.getDate() === day.dObj.getDate()
          );
          if (match) {
            match.clockIn += 1;
          }
        }
        if (l.checkedOutAt) {
          const d = new Date(l.checkedOutAt);
          const match = days.find(
            (day) =>
              d.getFullYear() === day.dObj.getFullYear() &&
              d.getMonth() === day.dObj.getMonth() &&
              d.getDate() === day.dObj.getDate()
          );
          if (match) {
            match.clockOut += 1;
          }
        }
      });

      return days.map((d) => ({ name: d.name, fullName: d.fullName, clockIn: d.clockIn, clockOut: d.clockOut }));
    }

    // Month: four 7-day buckets covering the last 28 days (Week 4 is the most recent).
    const weeks = [{ name: "Week 1" }, { name: "Week 2" }, { name: "Week 3" }, { name: "Week 4" }].map((w) => ({ ...w, clockIn: 0, clockOut: 0 }));
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const bucketOf = (iso) => {
      const d = new Date(iso);
      d.setHours(0, 0, 0, 0);
      const diffDays = Math.round((startOfToday.getTime() - d.getTime()) / 86400000);
      if (diffDays < 0 || diffDays >= ACCESS_CHART_DAYS) return null;
      return weeks[3 - Math.floor(diffDays / 7)];
    };
    (rawAccessLogs || []).forEach((l) => {
      if (l.checkedInAt) {
        const w = bucketOf(l.checkedInAt);
        if (w) w.clockIn += 1;
      }
      if (l.checkedOutAt) {
        const w = bucketOf(l.checkedOutAt);
        if (w) w.clockOut += 1;
      }
    });
    return weeks;
  }, [rawAccessLogs, trendPeriod]);

  const currentDateRangeText = useMemo(() => {
    const d = new Date();
    const startDay = "01";
    const monthStr = d.toLocaleDateString("en-US", { month: "short" });
    const yearStr = d.getFullYear();
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return `${startDay} ${monthStr} - ${lastDay} ${monthStr} ${yearStr}`;
  }, []);

  const tenantDetailModalRef = useRef(null);
  const requestDetailModalRef = useRef(null);
  const addTenantModalRef = useRef(null);
  const downloadTenantModalRef = useRef(null);
  const categoriesModalRef = useRef(null);

  const tenantDetailModal = useBootstrapModal(tenantDetailModalRef);
  const requestDetailModal = useBootstrapModal(requestDetailModalRef);
  const addTenantModal = useBootstrapModal(addTenantModalRef);
  const downloadTenantModal = useBootstrapModal(downloadTenantModalRef);
  const categoriesModal = useBootstrapModal(categoriesModalRef);

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchDashboardData = useCallback(async ({ silent = false } = {}) => {
    if (!estateId) {
      setPageReady(true);
      return;
    }

    if (!silent) {
      setIsLoadingTenants(true);
      setIsLoadingRequests(true);
    }

    const phaseAdmin = isPhaseAdmin();
    try {
      const [
        summaryRes,
        upcomingRentRes,
        requestsRes,
        activationRes,
        tenantsRes,
        accessLogsRes,
        buildingTypesRes,
        buildingsRes,
        propertiesRes,
      ] = await Promise.allSettled([
        authFetch(`${API_BASE}/estates/${estateId}/dashboard-summary`),
        authFetch(`${API_BASE}/estates/${estateId}/tenants/upcoming-rent?daysAhead=60&pageIndex=0&pageSize=50`),
        authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests?pageIndex=1&pageSize=50`),
        // Phase Administrators are forbidden from account activation, building types and phases.
        phaseAdmin ? Promise.resolve(null) : authFetch(`${API_BASE}/estates/${estateId}/account-activation-status?pageIndex=0&pageSize=1000`),
        authFetch(`${API_BASE}/estates/${estateId}/tenants/with-dependents?pageIndex=0&pageSize=1000`),
        fetchChartAccessLogs(estateId),
        phaseAdmin ? Promise.resolve(null) : authFetch(`${API_BASE}/estates/${estateId}/building-types`),
        authFetch(`${API_BASE}/estates/${estateId}/buildings`),
        authFetch(`${API_BASE}/estates/${estateId}/dashboard/properties`),
      ]);

      // Process building types, buildings, and phases
      let fetchedBuildingTypes = [];
      let fetchedBuildings = [];
      let fetchedPhases = [];

      if (buildingTypesRes?.status === "fulfilled" && buildingTypesRes.value?.ok) {
        try {
          const json = safeJsonParse(await buildingTypesRes.value.text());
          fetchedBuildingTypes = parseArrayData(json);
        } catch (e) {
          console.error("Building types parse error:", e);
        }
      }

      if (buildingsRes?.status === "fulfilled" && buildingsRes.value?.ok) {
        try {
          const json = safeJsonParse(await buildingsRes.value.text());
          fetchedBuildings = parseArrayData(json);
        } catch (e) {
          console.error("Buildings parse error:", e);
        }
      }

      if (propertiesRes?.status === "fulfilled" && propertiesRes.value?.ok) {
        try {
          const json = safeJsonParse(await propertiesRes.value.text());
          if (json?.data) {
            setPropertiesSummary(json.data);
            fetchedPhases = (json.data.phases || []).map((p) => ({ id: p.phaseId, name: p.phaseName, ...p }));
          }
        } catch (e) {
          console.error("Properties summary parse error:", e);
        }
      }

      setEstateBuildingTypes(fetchedBuildingTypes);
      setEstateBuildings(fetchedBuildings);
      setEstatePhases(fetchedPhases);

      if (summaryRes.status === "fulfilled" && summaryRes.value?.ok) {
        const json = safeJsonParse(await summaryRes.value.text());
        if (json?.data) {
          const d = json.data;
          setStats(p => ({
            ...p,
            totalRentCollected: d.totalRentCollected ?? 0,
            pendingRent: d.pendingRent ?? 0,
            propertiesManaged: d.propertiesManaged ?? 0,
            totalTenants: d.tenants ?? 0,
            owingTenantsCount: d.outstandingPaymentsCount ?? 0,
            visitorsToday: d.visitorsToday ?? d.accessLogsToday ?? d.todayVisitors ?? 0,
            passesIssued: d.passesIssued ?? 0,
            activeVisits: d.activeVisits ?? 0,
          }));
        }
      }

      const activationMap = new Map();
      if (activationRes.status === "fulfilled" && activationRes.value?.ok) {
        const actJson = safeJsonParse(await activationRes.value.text());
        const accountsList = actJson?.data?.accounts?.data || actJson?.data?.accounts || parseArrayData(actJson);
        if (Array.isArray(accountsList)) {
          accountsList.map(normalizeActivationAccount).forEach(acc => {
            if (acc.id) activationMap.set(String(acc.id).toLowerCase(), acc);
            if (acc.email) activationMap.set(String(acc.email).trim().toLowerCase(), acc);
          });
        }
      }

      let allTenants = [];
      if (tenantsRes.status === "fulfilled" && tenantsRes.value?.ok) {
        const json = safeJsonParse(await tenantsRes.value.text());
        allTenants = parseArrayData(json);
        setAllEstateTenants(allTenants);
      }

      const notActivatedList = [];
      const unassignedList = [];

      allTenants.forEach((t) => {
        const tid = String(t.tenantId || t.id || "").toLowerCase();
        const email = String(t.email || "").trim().toLowerCase();
        const actInfo = activationMap.get(tid) || (email ? activationMap.get(email) : null);
        const isActivated = actInfo
          ? String(actInfo.activationStatus || "").toLowerCase() === "activated"
          : String(t.activationStatus || "").toLowerCase() === "activated";

        const buildings = t.currentBuildings || [];
        const hasAssignedBuilding = Array.isArray(buildings) && buildings.length > 0;
        const b = buildings[0] || {};
        const typeName = b.typeName || t.buildingType || "Residential Unit";
        const titleStr = t.title ? `${t.title} ` : "";
        const fullName = `${titleStr}${t.firstName || ""} ${t.lastName || ""}`.trim() || t.name || "Resident";

        const tenantCard = {
          id: t.tenantId || t.id,
          name: fullName,
          firstName: t.firstName || "",
          lastName: t.lastName || "",
          title: t.title || "",
          email: t.email || "",
          phone: t.phoneNumber || t.phone || "",
          typeName,
          buildingNumber: b.number || t.buildingNumber || "Unassigned",
          phaseName: b.phaseName || t.phaseName || "Estate Grounds",
          house: hasAssignedBuilding ? `${b.phaseName || "Phase"} — ${b.number || "Unit"}` : "No Building Assigned",
          amount: "₦320,000",
          basicRentFormatted: "₦10,000,000",
          expiryISO: new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0],
          expiryLabel: fmtDate(new Date(Date.now() + 30 * 86400000).toISOString()),
          isCorporate: t.isCorporateTenant || false,
          imageUrl: t.imageUrl || null,
          buildingImageUrl: getHouseImage(typeName),
          isActivated,
          hasAssignedBuilding,
          metricLabel: hasAssignedBuilding ? "Active" : "Unassigned",
        };

        if (!isActivated) {
          notActivatedList.push({
            ...tenantCard,
            badgeClass: "badge-light-warning text-warning",
            badgeText: "Not Activated",
          });
        }

        if (!hasAssignedBuilding) {
          unassignedList.push({
            ...tenantCard,
            badgeClass: "badge-light-danger text-danger",
            badgeText: "Unassigned",
          });
        }
      });

      setNotActivatedTenants(notActivatedList);
      setUnassignedTenants(unassignedList);

      if (upcomingRentRes.status === "fulfilled" && upcomingRentRes.value?.ok) {
        const json = safeJsonParse(await upcomingRentRes.value.text());
        const items = json?.data?.data || parseArrayData(json);
        const pendingCards = [];

        items.forEach((t) => {
          const title = t.title || "";
          const fullName = `${title ? title + " " : ""}${t.firstName || ""} ${t.lastName || ""}`.trim() || t.name || "Resident";
          const buildingsList = (Array.isArray(t.upcomingRentBuildings) && t.upcomingRentBuildings.length > 0)
            ? t.upcomingRentBuildings
            : [{}];

          buildingsList.forEach((rentInfo, bIdx) => {
            const expiryISO = rentInfo.dueDate ? rentInfo.dueDate.split("T")[0] : new Date().toISOString().split("T")[0];
            const typeName = rentInfo.typeName || t.buildingType || "";
            const buildingImageUrl = rentInfo.buildingImageUrl || getHouseImage(typeName);
            const rentVal = rentInfo.totalAmount || rentInfo.rentAmount || t.totalAmountDue || 0;
            const formattedAmt = `₦${Number(rentVal).toLocaleString()}`;
            const houseStr = rentInfo.buildingNumber
              ? `${rentInfo.phaseName ? rentInfo.phaseName + " — " : ""}${rentInfo.buildingNumber}`
              : "—";
            const components = Array.isArray(rentInfo.components) ? rentInfo.components : [];
            const rentComp = components.find((c) =>
              ["type", "codeName", "name", "displayName"].some((k) => String(c?.[k] || "").toLowerCase() === "rent")
            );
            const basicRent = rentComp?.amount ?? rentInfo.rentAmount ?? rentVal;

            pendingCards.push({
              id: t.tenantId || t.id,
              cardId: `${t.tenantId || t.id}_${bIdx}`,
              name: fullName,
              firstName: t.firstName,
              lastName: t.lastName,
              title,
              phone: t.phoneNumber || t.phone || "",
              house: houseStr,
              buildingNumber: rentInfo.buildingNumber || "",
              phaseName: rentInfo.phaseName || "",
              typeName,
              buildingImageUrl,
              amount: formattedAmt,
              basicRentFormatted: `₦${Number(basicRent).toLocaleString()}`,
              email: t.email || "",
              buildingId: rentInfo.buildingId || "",
              outstandingInvoices: rentInfo.dueDate
                ? [{
                    rentAmount: rentInfo.rentAmount,
                    totalAmount: rentInfo.totalAmount,
                    dueDate: rentInfo.dueDate,
                    status: rentInfo.invoiceStatus || "Pending",
                    components,
                  }]
                : [],
              expiryISO,
              expiryLabel: fmtDate(expiryISO),
              isCorporate: t.isCorporateTenant || false,
              imageUrl: t.imageUrl || null,
              initials: getInitials(t.firstName || "", t.lastName || "") || getInitials(fullName),
              avatarClass: AVATAR_CLASSES[pendingCards.length % AVATAR_CLASSES.length],
              badgeClass: "badge-light-danger text-danger",
              badgeText: "Due Soon",
              metricLabel: "Pending Rent",
            });
          });
        });

        setPendingRentTenants(pendingCards);
      }

      if (requestsRes.status === "fulfilled" && requestsRes.value?.ok) {
        try {
          const json = safeJsonParse(await requestsRes.value.text());
          const items = parseArrayData(json).map(normalizeMaintenanceRequest);
          const allRequests = items.map(req => {
            const bNum = req.buildingNumber || req.house || req.building || "";
            let pName = req.phaseName || req.phase || req.phaseNumber || "";

            if (!pName && req.requesterCurrentBuildings && Array.isArray(req.requesterCurrentBuildings)) {
              const matchedB = req.requesterCurrentBuildings.find(
                b => String(b.number || b.buildingNumber || "").trim().toLowerCase() === String(bNum).trim().toLowerCase()
              ) || req.requesterCurrentBuildings[0];

              if (matchedB) {
                pName = matchedB.phaseName || matchedB.phase || "";
              }
            }

            if (!pName && Array.isArray(allTenants)) {
              const tid = String(req.tenantId || req.requesterId || "").toLowerCase();
              const matchedTenant = allTenants.find(t => String(t.tenantId || t.id || "").toLowerCase() === tid);
              if (matchedTenant?.currentBuildings && Array.isArray(matchedTenant.currentBuildings) && matchedTenant.currentBuildings.length > 0) {
                const matchedB = matchedTenant.currentBuildings.find(
                  b => String(b.number || b.buildingNumber || "").trim().toLowerCase() === String(bNum).trim().toLowerCase()
                ) || matchedTenant.currentBuildings[0];
                if (matchedB) {
                  pName = matchedB.phaseName || matchedB.phase || "";
                }
              }
            }

            if (pName && !String(pName).toLowerCase().startsWith("phase")) {
              pName = `Phase ${pName}`;
            }

            let bDisplay = bNum;
            if (bDisplay && !bDisplay.includes(" - ") && !bDisplay.includes(" — ") && !bDisplay.toLowerCase().startsWith("phase")) {
              bDisplay = pName ? `${pName} - ${bDisplay}` : bDisplay;
            } else if (!bDisplay) {
              bDisplay = pName ? `${pName} - Unassigned` : "Unassigned";
            }

            return {
              ...req,
              id: req.id,
              tenantId: req.tenantId,
              tenantName: req.tenantName || "Unknown Tenant",
              buildingNumber: bDisplay,
              phaseName: pName,
              requesterCurrentBuildings: req.requesterCurrentBuildings || [],
              title: capitalizeFirst(req.title || req.shortDescription || "Maintenance request"),
              description: capitalizeFirst(req.shortDescription || ""),
              meta: fmtDate(req.createdDate),
              priority: req.priorityLevel || "Normal",
              category: req.category || "General",
              status: req.status || "Created",
              uploadedFileUrls: req.uploadedFileUrls || [],
            };
          });

          setRequests(allRequests);
        } catch (e) {
          console.error("Dashboard requests parsing error:", e);
        }
      }

      if (accessLogsRes.status === "fulfilled" && accessLogsRes.value) {
        setRawAccessLogs(accessLogsRes.value.windowLogs);
        setInsideCount(accessLogsRes.value.insideCount);
      }

    } catch (e) {
      console.error("Dashboard fetch error:", e);
    } finally {
      setIsLoadingTenants(false);
      setIsLoadingRequests(false);
      setPageReady(true);
    }
  }, [estateId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleRemindAll = () => toast.success("Reminder sent to all tenants with pending rent.");
  const handleTenantClick = (tenant) => {
    setSelectedTenant(tenant);
    tenantDetailModal.show();
  };
  const handleRequestClick = (req) => {
    setSelectedRequest(req);
    requestDetailModal.show();
  };

  const sortedRentTenants = useMemo(
    () => [...pendingRentTenants].sort((a, b) => daysBetween(today, parseISODate(a.expiryISO)) - daysBetween(today, parseISODate(b.expiryISO))),
    [pendingRentTenants, today]
  );

  const activeRequests = useMemo(() => {
    return requests.filter(r => {
      const s = (r.status || "").toLowerCase();
      return !s.includes("complete") && !s.includes("resolve");
    });
  }, [requests]);

  const totalTenantsCount = allEstateTenants.length || stats.totalTenants || 0;
  const notActivatedCount = notActivatedTenants.length;
  const activatedCount = Math.max(0, totalTenantsCount - notActivatedCount);
  const activatedPct = totalTenantsCount ? Math.round((activatedCount / totalTenantsCount) * 100) : 0;

  const totalProps = stats.propertiesManaged || 0;
  const unassignedCount = unassignedTenants.length;
  const assignedCount = Math.max(0, totalProps - unassignedCount);
  const assignedPct = totalProps ? Math.round((assignedCount / totalProps) * 100) : 0;

  const rentSummaryTotal = stats.totalRentCollected + stats.pendingRent;
  const pendingRentValPct = rentSummaryTotal > 0 ? Math.round((stats.pendingRent / rentSummaryTotal) * 100) : 0;

  // Occupancy tab data: Current (Assigned vs Vacant) and Phase (All phases)
  const occupancyCurrentData = useMemo(() => {
    const fromServer = propertiesSummary && typeof propertiesSummary.totalUnits === "number";
    const totalHouses = fromServer
      ? propertiesSummary.totalUnits
      : (estateBuildings && estateBuildings.length > 0) ? estateBuildings.length : totalProps;

    const assignedCountCalculated = fromServer
      ? propertiesSummary.occupiedUnits ?? 0
      : (estateBuildings && estateBuildings.length > 0)
        ? estateBuildings.filter((b) => checkIsBuildingAssigned(b, allEstateTenants)).length
        : assignedCount;

    const safeAssigned = Math.min(totalHouses, Math.max(0, assignedCountCalculated));
    const safeVacant = Math.max(0, totalHouses - safeAssigned);
    const assignedPctCalc = totalHouses > 0 ? Math.round((safeAssigned / totalHouses) * 100) : 60;
    const vacantPctCalc = 100 - assignedPctCalc;

    return {
      total: totalHouses,
      assigned: safeAssigned,
      vacant: safeVacant,
      assignedPct: assignedPctCalc,
      vacantPct: vacantPctCalc,
      donut: [
        { name: "Assigned", value: safeAssigned || 60, color: "#10B981" },
        { name: "Vacant", value: safeVacant || 40, color: "#FACC15" },
      ],
    };
  }, [propertiesSummary, estateBuildings, allEstateTenants, totalProps, assignedCount]);

  const occupancyPhaseData = useMemo(() => {
    const totalHouses = (estateBuildings && estateBuildings.length > 0)
      ? estateBuildings.length
      : totalProps;

    const phasePalette = SERIES;

    let phasesList = [];
    if (estatePhases && estatePhases.length > 0) {
      phasesList = estatePhases.map((p, idx) => {
        const pBuildings = (estateBuildings || []).filter(
          (b) =>
            String(b.phaseId || "") === String(p.id || "") ||
            (b.phaseName && b.phaseName.trim().toLowerCase() === (p.name || "").trim().toLowerCase())
        );
        // Units per phase come from /dashboard/properties; buildings are the fallback.
        const count = typeof p.totalUnits === "number"
          ? p.totalUnits
          : pBuildings.length || Math.max(1, Math.round(totalHouses / (estatePhases.length || 1)));
        return {
          id: p.id || `phase-${idx}`,
          name: p.name || `Phase ${idx + 1}`,
          count,
          color: phasePalette[idx % phasePalette.length],
        };
      });
    } else if (estateBuildings && estateBuildings.length > 0) {
      const byPhase = new Map();
      estateBuildings.forEach((b) => {
        const name = b.phaseName || "Unphased";
        byPhase.set(name, (byPhase.get(name) || 0) + 1);
      });
      phasesList = [...byPhase.entries()]
        .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
        .map(([name, count], idx) => ({ id: `phase-${idx}`, name, count, color: phasePalette[idx % phasePalette.length] }));
    } else {
      phasesList = [];
    }

    const phasesSum = phasesList.reduce((acc, curr) => acc + curr.count, 0);
    const items = phasesList.map((p) => {
      const pct = phasesSum ? Math.round((p.count / phasesSum) * 100) : 0;
      return {
        ...p,
        pct,
        value: p.count,
      };
    });

    return {
      total: phasesSum,
      items,
      donut: items.map((p) => ({ name: p.name, value: p.value, color: p.color })),
    };
  }, [estatePhases, estateBuildings, totalProps]);

  const unitBreakdown = useMemo(() => {
    return [
      {
        label: "Pending Rent",
        displayPrimary: `₦${stats.pendingRent.toLocaleString()}`,
        badgeText: `${pendingRentValPct}% unpaid`,
        badgeClass: "bg-white text-danger border border-danger border-opacity-25",
        total: totalTenantsCount,
        occupiedLabel: "Pending",
        occupied: stats.owingTenantsCount || pendingRentTenants.length || 0,
        availableLabel: "Cleared",
        available: Math.max(0, totalTenantsCount - (stats.owingTenantsCount || pendingRentTenants.length || 0)),
        occupiedPct: pendingRentValPct,
        dotColor: "#EF4444",
        gradient: "linear-gradient(90deg, #EF4444, #FCA5A5)",
        icon: "ki-information-2",
        iconColor: "text-danger",
        cardClass: "bg-light-danger",
        cardBg: "#FEF2F2",
      },
      {
        label: "Tenant Activations",
        displayPrimary: `${activatedCount} / ${totalTenantsCount}`,
        badgeText: `${activatedPct}% activated`,
        badgeClass: "bg-white text-success border border-success border-opacity-25",
        total: totalTenantsCount,
        occupiedLabel: "Activated",
        occupied: activatedCount,
        availableLabel: "Inactive",
        available: notActivatedCount,
        occupiedPct: activatedPct,
        dotColor: "#10B981",
        gradient: "linear-gradient(90deg, #10B981, #A7F3D0)",
        icon: "ki-user-tick",
        iconColor: "text-success",
        cardClass: "bg-light-success",
        cardBg: "#ECFDF5",
      },
      {
        label: "Property Occupancy",
        displayPrimary: `${assignedCount} / ${totalProps}`,
        badgeText: `${assignedPct}% occupied`,
        badgeClass: "bg-white text-warning border border-warning border-opacity-25",
        total: totalProps,
        occupiedLabel: "Assigned",
        occupied: assignedCount,
        availableLabel: "Unassigned",
        available: unassignedCount,
        occupiedPct: assignedPct,
        dotColor: "#F59E0B",
        gradient: "linear-gradient(90deg, #F59E0B, #FDE68A)",
        icon: "ki-bank",
        iconColor: "text-warning",
        cardClass: "bg-light-warning",
        cardBg: "#FFFBEB",
      },
    ];
  }, [
    stats.pendingRent,
    stats.owingTenantsCount,
    pendingRentTenants.length,
    pendingRentValPct,
    totalTenantsCount,
    activatedCount,
    notActivatedCount,
    activatedPct,
    assignedCount,
    unassignedCount,
    totalProps,
    assignedPct
  ]);

  const [searchInputText, setSearchInputText] = useState("");
  const [executedSearchQuery, setExecutedSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  const handleExecuteSearch = useCallback(() => {
    const q = (searchInputText || "").trim();
    if (!q) {
      setExecutedSearchQuery("");
      setIsSearchOpen(false);
      return;
    }
    setExecutedSearchQuery(q);
    setIsSearchOpen(true);
  }, [searchInputText]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const systemPages = useMemo(() => [
    { title: "Dashboard", path: "/dashboard", desc: "Estate overview & metrics", icon: "ki-home" },
    { title: "Rent Management", path: "/rent-management", desc: "Invoices, payments & collections", icon: "ki-document" },
    { title: "Tenant Management", path: "/tenant-management", desc: "Directory & resident profiles", icon: "ki-user-tick" },
    { title: "Estate Access Logs", path: "/estate-access", desc: "Check-in/out logs & visitor passes", icon: "ki-shield-tick" },
    { title: "Car Management", path: "/car-management", desc: "Vehicles, decals & parking tags", icon: "ki-car" },
    { title: "Security Incidents", path: "/security-incidents", desc: "Security blotter & incident reports", icon: "ki-information-2" },
    { title: "Reports & Analytics", path: "/reports", desc: "Summary spreadsheets & exports", icon: "ki-file-down" },
    ...(isPhaseAdmin() ? [] : [{ title: "Estate Setup", path: "/estate-setup", desc: "Properties, phases & zones", icon: "ki-bank" }]),
  ], []);

  const searchResults = useMemo(() => {
    const q = (executedSearchQuery || "").trim().toLowerCase();
    if (!q) return { tenants: [], requests: [], properties: [], pages: [] };

    const matchedTenants = (allEstateTenants || []).filter((t) => {
      const name = `${t.firstName || ""} ${t.lastName || ""} ${t.name || ""}`.toLowerCase();
      const email = (t.email || "").toLowerCase();
      const phone = (t.phoneNumber || t.phone || "").toLowerCase();
      const unit = (t.buildingNumber || t.house || "").toLowerCase();
      const phase = (t.phaseName || "").toLowerCase();
      return name.includes(q) || email.includes(q) || phone.includes(q) || unit.includes(q) || phase.includes(q);
    }).slice(0, 5);

    const matchedRequests = (requests || []).filter((r) => {
      const title = (r.title || "").toLowerCase();
      const desc = (r.description || "").toLowerCase();
      const category = (r.category || r.requestType || "").toLowerCase();
      const tenant = (r.tenantName || r.reportedBy || "").toLowerCase();
      const status = (r.status || "").toLowerCase();
      return title.includes(q) || desc.includes(q) || category.includes(q) || tenant.includes(q) || status.includes(q);
    }).slice(0, 5);

    const matchedProperties = (allHouseTypes || []).filter((h) => {
      const name = (h.name || h.typeName || "").toLowerCase();
      const phase = (h.phaseName || "").toLowerCase();
      return name.includes(q) || phase.includes(q);
    }).slice(0, 4);

    const matchedPages = systemPages.filter((p) => {
      return p.title.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q);
    }).slice(0, 4);

    return {
      tenants: matchedTenants,
      requests: matchedRequests,
      properties: matchedProperties,
      pages: matchedPages,
    };
  }, [executedSearchQuery, allEstateTenants, requests, allHouseTypes, systemPages]);

  const totalSearchMatches =
    searchResults.tenants.length +
    searchResults.requests.length +
    searchResults.properties.length +
    searchResults.pages.length;

  const handleSelectTenantResult = (tenant) => {
    setSelectedTenant(tenant);
    tenantDetailModal.show();
    setIsSearchOpen(false);
    setSearchInputText("");
    setExecutedSearchQuery("");
  };

  const handleSelectRequestResult = (req) => {
    setSelectedRequest(req);
    requestDetailModal.show();
    setIsSearchOpen(false);
    setSearchInputText("");
    setExecutedSearchQuery("");
  };

  const handleSelectPropertyResult = (houseIndex) => {
    setActiveHouseIndex(houseIndex);
    setHouseAnimKey((k) => k + 1);
    setIsSearchOpen(false);
    setSearchInputText("");
    setExecutedSearchQuery("");
    const el = document.querySelector(".spotlight-card");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSelectPageResult = (path) => {
    navigate(path);
    setIsSearchOpen(false);
    setSearchInputText("");
    setExecutedSearchQuery("");
  };

  const accessTotals = useMemo(() => {
    const totalIn = accessTrendData.reduce((acc, curr) => acc + (curr.clockIn || 0), 0);
    const totalOut = accessTrendData.reduce((acc, curr) => acc + (curr.clockOut || 0), 0);
    const busiest = accessTrendData.reduce((best, curr) => ((curr.clockIn || 0) > (best?.clockIn || 0) ? curr : best), null);
    const peak = busiest && (busiest.clockIn || 0) > 0 ? busiest : null;
    return { totalIn, totalOut, onSite: insideCount, peak };
  }, [accessTrendData, insideCount]);




  const getRequestStatusMeta = (r) => {
    const s = (r.status || "").toLowerCase();
    if (s.includes("cancel") || s.includes("reject")) {
      return { label: "Canceled", bar: "bg-danger", text: "text-danger", hoverBorder: "border-hover-danger" };
    }
    return { label: "In Progress", bar: "bg-warning", text: "text-warning", hoverBorder: "border-hover-warning" };
  };


  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        :root {
          --font-sans: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          --primary-green: #0EA84A;
        }

        #kt_app_root, #kt_app_root * {
          font-family: var(--font-sans);
        }

        *, *::before, *::after,
        .card-surface,
        .stat-subcard,
        .custom-card-modal,
        .btn,
        .card,
        .card-flush,
        .modal-content {
          box-shadow: none !important;
        }

        .card-surface {
          background: #ffffff;
          border-radius: 16px;
          border: 1px solid #f1f5f9;
        }

        .stat-subcard {
          border-radius: 16px;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        /* Prevent and remove any blue outline/border when focusing or clicking on recharts */
        .recharts-wrapper,
        .recharts-wrapper *,
        .recharts-wrapper:focus,
        .recharts-wrapper:focus-visible,
        .recharts-wrapper:active,
        .recharts-surface,
        .recharts-surface:focus,
        .recharts-surface:focus-visible,
        .recharts-responsive-container,
        .recharts-responsive-container:focus,
        .recharts-responsive-container:focus-visible {
          outline: none !important;
          outline-style: none !important;
          border: none !important;
          box-shadow: none !important;
        }

        /* Ensure Bootstrap tooltips have white backgrounds */
        .tooltip .tooltip-inner {
          background-color: #ffffff !important;
          color: #0f172a !important;
          border: 1px solid #e2e8f0 !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
          font-weight: 600;
          font-size: 11px;
        }
        .tooltip.bs-tooltip-top .tooltip-arrow::before,
        .tooltip.bs-tooltip-auto[data-popper-placement^="top"] .tooltip-arrow::before {
          border-top-color: #ffffff !important;
        }
        .tooltip.bs-tooltip-bottom .tooltip-arrow::before,
        .tooltip.bs-tooltip-auto[data-popper-placement^="bottom"] .tooltip-arrow::before {
          border-bottom-color: #ffffff !important;
        }

        .hero-stats-strip {
          background: linear-gradient(135deg, #065F46 0%, #0F9D58 45%, #12B76A 100%) !important;
          border-radius: 16px !important;
          border: none !important;
          padding: 20px 24px !important;
        }

        @media (min-width: 768px) {
          .hero-stat-col:not(:last-child) {
            border-right: 1px solid rgba(255, 255, 255, 0.25);
          }
        }

        /* Architectural Ken Burns Zoom Animation */
        @keyframes kenBurnsSlowZoom {
          0% {
            transform: scale(1.18);
          }
          100% {
            transform: scale(1.0);
          }
        }

        .ken-burns-hero {
          animation: kenBurnsSlowZoom 20s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          will-change: transform;
        }

        @keyframes residentPillFade {
          0% {
            opacity: 0.3;
            transform: translateY(6px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .resident-pill-anim {
          animation: residentPillFade 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        /* Vibrant lime / light green mesh card based on user image */
        .rent-revenue-mesh-card {
          background: #a3e635;
          background:
            radial-gradient(circle at 82% 18%, rgba(217, 249, 157, 0.95) 0%, transparent 42%),
            radial-gradient(circle at 18% 15%, rgba(190, 242, 100, 0.95) 0%, transparent 48%),
            radial-gradient(circle at 8% 82%, rgba(132, 204, 22, 0.9) 0%, transparent 52%),
            radial-gradient(circle at 88% 85%, rgba(74, 222, 128, 0.85) 0%, transparent 48%),
            radial-gradient(circle at 48% 45%, rgba(163, 230, 53, 0.9) 0%, transparent 50%),
            linear-gradient(135deg, #bef264 0%, #a3e635 45%, #84cc16 100%);
          border-radius: 24px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 12px 30px -10px rgba(132, 204, 22, 0.35);
        }

        .rent-revenue-mesh-card::before {
          content: "";
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle, rgba(255, 255, 255, 0.3) 12%, transparent 12.5%);
          background-size: 16px 16px;
          opacity: 0.25;
          pointer-events: none;
        }

        /* Animated arrow button */
        .d6-arrow-btn {
          transition: all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
          cursor: pointer;
        }
        .d6-arrow-btn:hover {
          transform: scale(1.14);
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
        }
        .d6-arrow-icon {
          transition: transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
          display: inline-block;
        }
        .d6-arrow-btn:hover .d6-arrow-icon {
          transform: translate(2px, 2px) scale(1.18);
        }

        /* Search pill wrapper */
        .search-pill-wrapper {
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
        }
        .search-pill-wrapper:focus-within {
          border-color: #10B981 !important;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15) !important;
        }

        .spotlight-card {
          border-radius: 16px;
          min-height: 340px;
          position: relative;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .spotlight-card:hover .spotlight-bg-img:not(.ken-burns-hero) {
          transform: scale(1.06);
        }

        .spotlight-bg-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.92;
        }

        .spotlight-nav-btn {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          border: none;
          background: rgba(255,255,255,0.25);
          backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease;
        }

        .spotlight-nav-btn:hover {
          background: rgba(255,255,255,0.45);
        }

        .spotlight-resident-pill {
          background: rgba(22, 26, 32, 0.72);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 9999px;
        }

        .spotlight-indicator-dot {
          border: none;
          padding: 0;
          height: 7px;
          border-radius: 999px;
          transition: width 0.3s ease;
        }

        .progress-track {
          width: 100%;
          height: 10px;
          border-radius: 999px;
          background: #e2e8f0;
          overflow: hidden;
        }

        .progress-fill-gradient {
          height: 100%;
          border-radius: 999px;
        }

        .legend-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          display: inline-block;
        }

        .insights-bg {
          background-image: url(/assets/media/zangon/imageb.jpg);
          background-size: cover;
          background-position: center;
        }

        .insights-overlay {
          background: linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 45%, rgba(0,0,0,0.55) 100%);
        }

        #kt_app_root { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
        #kt_app_header .fw-bolder, #kt_app_content_container .fw-bolder,
        #kt_app_header .fw-bold, #kt_app_content_container .fw-bold { font-weight: 600 !important; }
        #kt_app_header .fw-semibold, #kt_app_content_container .fw-semibold { font-weight: 500 !important; }
        #kt_app_content_container .fw-medium { font-weight: 400 !important; }
        #kt_app_header .btn, #kt_app_content_container .btn { font-weight: 500 !important; font-size: 13px !important; }
        #kt_app_content_container .fs-6 { font-size: 15px !important; }
        #kt_app_content_container .fs-7 { font-size: 14px !important; }
        #kt_app_content_container .fs-8 { font-size: 13px !important; }
        #kt_app_content_container .fs-9 { font-size: 11.5px !important; }

        .d6-page-title { font-size: 20px; font-weight: 600; letter-spacing: -0.01em; }
        .d6-greeting { font-size: 28px; font-weight: 500; letter-spacing: -0.02em; line-height: 1.15; }
        .d6-title { font-size: 16px; font-weight: 600; letter-spacing: -0.005em; line-height: 1.3; }
        .d6-hero-title { font-size: 28px; font-weight: 500; letter-spacing: -0.02em; line-height: 1.15; }
        .d6-num { font-size: 30px; font-weight: 500; letter-spacing: -0.02em; line-height: 1; }
        .d6-num-md { font-size: 24px; font-weight: 500; letter-spacing: -0.02em; line-height: 1; }
        .d6-num-suffix { font-size: 16px; font-weight: 400; letter-spacing: 0; }

        .d6-mbtn {
          height: 40px;
          padding: 0 20px !important;
          border-radius: 999px !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
        }
        .d6-mbtn.btn-light { background: #f4f4f5 !important; color: #3f3f46 !important; border: 1px solid #e4e4e7 !important; }
        .d6-mbtn.btn-light:hover { background: #e9e9ec !important; color: #18181b !important; }
        .d6-mbtn i { color: inherit; }

        /* ---------- modal system ---------- */
        .d6m-dialog { width: calc(100% - 24px); margin-left: auto; margin-right: auto; }
        .d6m {
          height: min(88vh, 860px);
          display: flex;
          flex-direction: column;
          border: 0 !important;
          border-radius: 20px !important;
          overflow: hidden;
          position: relative;
          background: #ffffff;
          color: #18181b;
          box-shadow: 0 30px 80px -20px rgba(15, 23, 42, 0.35) !important;
        }
        .d6m-head { display: flex; align-items: center; gap: 14px; padding: 20px 24px; border-bottom: 1px solid #f0f0f2; flex-shrink: 0; }
        .d6m-head-icon {
          width: 40px; height: 40px; border-radius: 12px; background: #f4f4f5; color: #3f3f46;
          display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .d6m-head-icon i { color: inherit; font-size: 1.3rem; }
        .d6m-title { font-size: 17px; font-weight: 600; letter-spacing: -0.01em; color: #18181b; margin: 0; line-height: 1.3; }
        .d6m-x {
          width: 36px; height: 36px; border-radius: 999px; border: 0; background: transparent; color: #71717a;
          display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; transition: background 0.15s ease, color 0.15s ease;
        }
        .d6m-x:hover { background: #f4f4f5; color: #18181b; }
        .d6m-x i { color: inherit; font-size: 1.2rem; }
        .d6m-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 24px; }
        .d6m-body-fill { display: flex; flex-direction: column; padding: 0; overflow: hidden; }
        .d6m-foot {
          display: flex; align-items: center; justify-content: flex-end; gap: 8px;
          padding: 14px 24px; border-top: 1px solid #f0f0f2; background: #ffffff; flex-shrink: 0;
        }

        .d6m-tabs { display: flex; gap: 20px; padding: 0 24px; border-bottom: 1px solid #f0f0f2; flex-shrink: 0; }
        .d6m-tabs button {
          border: 0; background: none; padding: 12px 0 11px; font-size: 13.5px; font-weight: 500; color: #71717a;
          border-bottom: 2px solid transparent; display: inline-flex; align-items: center; transition: color 0.15s ease, border-color 0.15s ease;
        }
        .d6m-tabs button:hover { color: #18181b; }
        .d6m-tabs button.active { color: #18181b; border-bottom-color: #18181b; }

        .d6m-person { display: flex; align-items: center; gap: 14px; }
        .d6m-avatar {
          border-radius: 999px; background: #f4f4f5; color: #52525b; font-weight: 600; flex-shrink: 0; overflow: hidden;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .d6m-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .d6m-name { font-size: 15.5px; font-weight: 600; color: #18181b; line-height: 1.3; }
        .d6m-muted { font-size: 12.5px; color: #71717a; line-height: 1.45; }
        .d6m-k { display: block; font-size: 12px; color: #71717a; margin-bottom: 4px; }
        .d6m-v { font-size: 14px; font-weight: 500; color: #18181b; }
        .d6m-dot { width: 7px; height: 7px; border-radius: 999px; display: inline-block; flex-shrink: 0; }
        .d6m-chip {
          display: inline-flex; align-items: center; gap: 7px; padding: 5px 11px; border-radius: 999px;
          background: #f4f4f5; color: #3f3f46; font-size: 12.5px; font-weight: 500; white-space: nowrap; flex-shrink: 0;
        }
        .d6m-chip::before { content: ""; width: 7px; height: 7px; border-radius: 999px; background: var(--dot, #a1a1aa); }
        .d6m-count {
          display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px; padding: 0 6px;
          border-radius: 999px; background: #f4f4f5; color: #52525b; font-size: 11.5px; font-weight: 600;
        }

        .d6m-kv { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); border: 1px solid #ececef; border-radius: 14px; overflow: hidden; }
        .d6m-kv-cell { padding: 14px 16px; min-width: 0; }
        .d6m-kv-cell + .d6m-kv-cell { border-left: 1px solid #ececef; }
        .d6m-kv-4 .d6m-kv-cell:nth-child(3) { border-left: 0; }
        .d6m-kv-4 .d6m-kv-cell:nth-child(n+3) { border-top: 1px solid #ececef; }

        .d6m-section { margin-top: 28px; }
        .d6m-section-head {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          font-size: 13px; font-weight: 600; color: #3f3f46; margin-bottom: 12px;
        }
        .d6m-section-head .d6m-muted { font-weight: 400; }
        .d6m-empty { padding: 28px 16px; text-align: center; color: #a1a1aa; font-size: 13px; border: 1px dashed #e4e4e7; border-radius: 14px; }
        .d6m-hint { font-size: 12px; color: #a1a1aa; margin-top: 6px; }
        .d6m-error { font-size: 12px; color: #dc2626; margin-top: 6px; }

        .d6m-invoice { border: 1px solid #ececef; border-radius: 14px; padding: 16px; }
        .d6m-invoice + .d6m-invoice { margin-top: 10px; }
        .d6m-amount { font-size: 22px; font-weight: 500; letter-spacing: -0.02em; line-height: 1.1; color: #18181b; }
        .d6m-lines { margin-top: 14px; background: #fafafa; border-radius: 10px; padding: 4px 14px; }
        .d6m-line { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 9px 0; font-size: 13px; color: #52525b; }
        .d6m-line span:last-child { color: #18181b; font-variant-numeric: tabular-nums; }
        .d6m-line + .d6m-line { border-top: 1px solid #f0f0f2; }
        .d6m-line-total { font-weight: 600; color: #18181b; }

        .d6m-property { display: flex; align-items: center; gap: 14px; border: 1px solid #ececef; border-radius: 14px; padding: 12px; }
        .d6m-property img { width: 64px; height: 64px; border-radius: 10px; object-fit: cover; flex-shrink: 0; }

        .d6m-issue-title { font-size: 15px; font-weight: 600; color: #18181b; line-height: 1.45; }
        .d6m-issue-desc { font-size: 14px; color: #52525b; line-height: 1.65; margin: 6px 0 0; white-space: pre-line; }
        .d6m-attachments { display: grid; grid-template-columns: repeat(auto-fill, minmax(88px, 1fr)); gap: 10px; }
        .d6m-thumb { aspect-ratio: 1; border: 1px solid #ececef; border-radius: 12px; overflow: hidden; padding: 0; background: #fafafa; transition: transform 0.2s ease; }
        .d6m-thumb:hover { transform: translateY(-2px); }
        .d6m-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .d6m-file {
          display: inline-flex; align-items: center; gap: 8px; padding: 0 12px; height: 40px; border: 1px solid #ececef; border-radius: 10px;
          font-size: 13px; color: #3f3f46; grid-column: span 2;
        }
        .d6m-file i { color: #71717a; }

        .d6m-seg { display: flex; gap: 4px; padding: 4px; background: #f4f4f5; border-radius: 999px; }
        .d6m-seg button {
          flex: 1; height: 38px; border: 0; background: transparent; border-radius: 999px; color: #52525b;
          font-size: 13px; font-weight: 500; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
        }
        .d6m-seg button i { color: inherit; font-size: 1.05rem; }
        .d6m-seg button:hover:not(:disabled) { color: #18181b; }
        .d6m-seg button.active { background: #ffffff; color: #18181b; box-shadow: 0 1px 3px rgba(0,0,0,0.08) !important; }

        .d6m-thread { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 20px 24px; display: flex; flex-direction: column; gap: 16px; }
        .d6m-thread > .d6m-empty { margin: auto 0; border: 0; }
        .d6m-msg { display: flex; align-items: flex-end; gap: 10px; max-width: 85%; }
        .d6m-msg.mine { align-self: flex-end; flex-direction: row-reverse; }
        .d6m-msg-body { min-width: 0; }
        .d6m-msg-meta { display: flex; gap: 8px; font-size: 11.5px; color: #a1a1aa; margin-bottom: 4px; }
        .d6m-msg-meta span:first-child { color: #52525b; font-weight: 500; }
        .d6m-msg.mine .d6m-msg-meta { justify-content: flex-end; }
        .d6m-bubble {
          background: #ffffff; border: 1px solid #ececef; border-radius: 14px 14px 14px 4px; padding: 10px 14px;
          font-size: 13.5px; line-height: 1.55; color: #27272a; white-space: pre-line; word-break: break-word;
        }
        .d6m-msg.mine .d6m-bubble { background: #f4f4f5; border-color: #f4f4f5; border-radius: 14px 14px 4px 14px; }
        .d6m-composer { display: flex; align-items: flex-end; gap: 10px; padding: 14px 24px; border-top: 1px solid #f0f0f2; flex-shrink: 0; }
        .d6m-composer textarea {
          flex: 1; resize: none; border: 1px solid #e4e4e7; border-radius: 14px; padding: 10px 14px; font-size: 13.5px;
          line-height: 1.5; outline: none; transition: border-color 0.15s ease, box-shadow 0.15s ease; min-height: 44px; max-height: 140px;
        }
        .d6m-composer textarea:focus { border-color: #a1a1aa; box-shadow: 0 0 0 3px rgba(24, 24, 27, 0.06); }

        .d6m-field label { display: block; font-size: 12.5px; font-weight: 500; color: #3f3f46; margin-bottom: 6px; }
        .d6m-input, .d6m-input-group {
          width: 100%; min-height: 42px; border: 1px solid #e4e4e7; border-radius: 10px; background: #ffffff;
          font-size: 14px; color: #18181b; transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .d6m-input { padding: 9px 12px; outline: none; }
        textarea.d6m-input { resize: vertical; line-height: 1.5; }
        select.d6m-input { appearance: auto; }
        .d6m-input::placeholder, .d6m-input-group input::placeholder { color: #a1a1aa; }
        .d6m-input:focus, .d6m-input-group:focus-within { border-color: #a1a1aa; box-shadow: 0 0 0 3px rgba(24, 24, 27, 0.06); }
        .d6m-input.is-readonly { background: #fafafa; color: #52525b; }
        .d6m-input.is-invalid, .d6m-input-group.is-invalid { border-color: #fca5a5; }
        .d6m-input-group { display: flex; align-items: center; overflow: hidden; }
        .d6m-input-group span { padding: 0 12px; color: #71717a; font-size: 14px; border-right: 1px solid #ececef; align-self: stretch; display: flex; align-items: center; background: #fafafa; }
        .d6m-input-group input { flex: 1; border: 0; outline: none; padding: 9px 12px; font-size: 14px; background: transparent; min-width: 0; }

        .d6m-upload { display: flex; align-items: center; gap: 14px; padding: 14px; border: 1px solid #ececef; border-radius: 14px; }
        .d6m-upload-preview {
          width: 56px; height: 56px; border-radius: 999px; border: 1px dashed #d4d4d8; background: #fafafa; color: #a1a1aa; padding: 0;
          display: inline-flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0;
        }
        .d6m-upload-preview img { width: 100%; height: 100%; object-fit: cover; }
        .d6m-upload-preview i { color: inherit; font-size: 1.5rem; }
        .d6m-link-btn { border: 0; background: none; padding: 0; font-size: 13px; font-weight: 500; color: #52525b; text-decoration: underline; text-underline-offset: 3px; }
        .d6m-link-btn:hover { color: #18181b; }
        .d6-mbtn-sm { height: 34px !important; padding: 0 14px !important; }

        .d6m-note {
          display: flex; gap: 10px; align-items: flex-start; margin-top: 28px; padding: 12px 14px; border-radius: 12px;
          background: #fafafa; color: #52525b; font-size: 12.5px; line-height: 1.5;
        }
        .d6m-note i { color: #a1a1aa; font-size: 1.1rem; margin-top: 1px; }

        .d6m-card { border: 1px solid #ececef; border-radius: 16px; padding: 18px; }
        .d6m-list { display: flex; flex-direction: column; border: 1px solid #ececef; border-radius: 14px; overflow: hidden; }
        .d6m-list-row {
          display: flex; align-items: center; gap: 12px; width: 100%; padding: 12px 14px; border: 0; background: #ffffff;
          text-align: left; cursor: pointer; transition: background 0.15s ease; margin: 0;
        }
        .d6m-list-row + .d6m-list-row { border-top: 1px solid #f0f0f2; }
        .d6m-list-row:hover { background: #fafafa; }
        .d6m-list-row.active { background: #f7f7f8; }
        .d6m-tile { width: 40px; height: 40px; border-radius: 10px; background: #f4f4f5; color: #3f3f46; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .d6m-tile i { color: inherit; font-size: 1.25rem; }
        .d6m-edit { font-size: 12.5px; color: #a1a1aa; font-weight: 500; flex-shrink: 0; }
        .d6m-list-row:hover .d6m-edit, .d6m-list-row.active .d6m-edit { color: #18181b; }
        .d6m-check-row input { position: absolute; opacity: 0; pointer-events: none; }
        .d6m-check {
          width: 20px; height: 20px; border-radius: 6px; border: 1.5px solid #d4d4d8; background: #ffffff; flex-shrink: 0;
          display: inline-flex; align-items: center; justify-content: center; transition: background 0.15s ease, border-color 0.15s ease;
        }
        .d6m-check svg { width: 14px; height: 14px; fill: none; stroke: #ffffff; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; opacity: 0; transition: opacity 0.15s ease; }
        .d6m-check-row.active .d6m-check { background: #18181b; border-color: #18181b; }
        .d6m-check-row.active .d6m-check svg { opacity: 1; }
        .d6m-check-row:focus-within .d6m-check { box-shadow: 0 0 0 3px rgba(24, 24, 27, 0.12); }

        .d6m-shimmer {
          display: inline-block;
          background: linear-gradient(90deg, #f1f1f3 0%, #f8f8fa 50%, #f1f1f3 100%);
          background-size: 200% 100%;
          animation: d6mShimmer 1.3s ease-in-out infinite;
          flex-shrink: 0;
        }
        .d6m-shimmer.mt-2, .d6m-shimmer.mt-3 { display: block; }
        @keyframes d6mShimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
        .d6m .dropdown .form-select { min-height: 42px; border-radius: 10px; border-color: #e4e4e7; font-size: 14px; }

        @media (max-width: 575.98px) {
          .d6m { height: calc(100vh - 24px); }
          .d6m-kv { grid-template-columns: minmax(0, 1fr); }
          .d6m-kv-cell + .d6m-kv-cell { border-left: 0; border-top: 1px solid #ececef; }
        }

        .d6-mbtn-sent { background: #065d38 !important; border-color: #065d38 !important; }
        .d6-mini-tick { width: 16px; height: 16px; fill: none; stroke: #ffffff; stroke-width: 2.6; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: 24; stroke-dashoffset: 24; animation: d6Draw 0.4s ease forwards; }
        .d6-bubble-in { animation: d6BubbleIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes d6BubbleIn { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: 1; transform: none; } }

        /* ---------- success state ---------- */
        .d6-success {
          position: absolute; inset: 0; z-index: 30;
          display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;
          padding: 32px;
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
          animation: d6FadeIn 0.3s ease both;
        }
        .d6-success-burst { position: relative; width: 220px; height: 220px; display: flex; align-items: center; justify-content: center; }
        .d6-success-ring { position: absolute; border-radius: 999px; border: 1px solid rgba(16, 185, 129, 0.14); background: rgba(236, 253, 245, 0.35); }
        .d6-success-ring-lg { inset: 0; animation: d6RingPulse 2.6s ease-out infinite; }
        .d6-success-ring-md { inset: 28px; animation: d6RingPulse 2.6s ease-out 0.35s infinite; }
        .d6-success-dot { position: absolute; border-radius: 999px; background: #86efac; opacity: 0; animation: d6DotFloat 2.4s ease-in-out infinite; }
        .d6-success-check { position: relative; width: 132px; height: 132px; animation: d6Pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
        .d6-success-halo { fill: none; stroke: #a7f3d0; stroke-width: 2; opacity: 0.8; }
        .d6-success-fill { fill: #ecfdf5; }
        .d6-success-stroke {
          fill: none; stroke: #10b981; stroke-width: 6; stroke-linecap: round;
          stroke-dasharray: 277; stroke-dashoffset: 277;
          transform: rotate(-90deg); transform-origin: 60px 60px;
          animation: d6Draw 0.7s cubic-bezier(0.65, 0, 0.35, 1) 0.1s forwards;
        }
        .d6-success-tick {
          fill: none; stroke: #059669; stroke-width: 8; stroke-linecap: round; stroke-linejoin: round;
          stroke-dasharray: 64; stroke-dashoffset: 64;
          animation: d6Draw 0.45s cubic-bezier(0.65, 0, 0.35, 1) 0.6s forwards;
        }
        .d6-success-title { margin: 18px 0 8px; font-size: 30px; font-weight: 500; letter-spacing: -0.02em; color: #0f172a; animation: d6Rise 0.5s ease 0.5s both; }
        .d6-success-msg { max-width: 420px; margin: 0; font-size: 15px; line-height: 1.6; color: #64748b; animation: d6Rise 0.5s ease 0.62s both; }
        .d6-success-progress { width: 180px; height: 4px; border-radius: 999px; background: #eef2f0; overflow: hidden; margin-top: 36px; }
        .d6-success-progress span { display: block; height: 100%; width: 0; border-radius: 999px; background: #10b981; animation-name: d6Progress; animation-timing-function: linear; animation-fill-mode: forwards; }
        @keyframes d6Draw { to { stroke-dashoffset: 0; } }
        @keyframes d6Pop { from { opacity: 0; transform: scale(0.6); } to { opacity: 1; transform: scale(1); } }
        @keyframes d6Rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes d6FadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes d6Progress { from { width: 0; } to { width: 100%; } }
        @keyframes d6RingPulse { 0% { transform: scale(0.92); opacity: 0; } 30% { opacity: 1; } 100% { transform: scale(1.06); opacity: 0; } }
        @keyframes d6DotFloat { 0%, 100% { opacity: 0; transform: translateY(4px); } 40%, 60% { opacity: 0.9; transform: translateY(-4px); } }
        @media (prefers-reduced-motion: reduce) {
          .d6-success *, .d6-success, .d6-bubble-in, .d6-mini-tick { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
          .d6-success-stroke, .d6-success-tick, .d6-mini-tick { stroke-dashoffset: 0; }
        }

        .d6-list-card { border-radius: 16px !important; }

        .d6-card {
          background: #ffffff;
          border: 1px solid #eceef1;
          border-radius: 16px;
          transition: border-color 0.2s ease, transform 0.2s ease;
        }
        .d6-card:hover { border-color: #d9dee4; }

        .d6-kpi {
          min-height: 205px;
        }
        .d6-kpi-brand {
          background:
            radial-gradient(circle at 88% 12%, rgba(163, 230, 53, 0.45) 0%, transparent 45%),
            radial-gradient(circle at 10% 100%, rgba(6, 93, 56, 0.9) 0%, transparent 60%),
            linear-gradient(135deg, #0EA84A 0%, #0B7F3B 55%, #065d38 100%);
          border: 1px solid rgba(6, 93, 56, 0.4);
          border-radius: 16px;
          color: #ffffff;
          position: relative;
          overflow: hidden;
        }
        .d6-kpi-brand::after {
          content: "";
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle, rgba(255, 255, 255, 0.35) 1px, transparent 1.5px);
          background-size: 14px 14px;
          -webkit-mask-image: linear-gradient(200deg, #000 0%, transparent 55%);
          mask-image: linear-gradient(200deg, #000 0%, transparent 55%);
          pointer-events: none;
        }

        .d6-icon-btn {
          width: 32px;
          height: 32px;
          border-radius: 10px;
          border: 1px solid #e4e7eb;
          background: #ffffff;
          color: #71717a;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
          text-decoration: none;
          flex-shrink: 0;
        }
        .d6-icon-btn i { color: inherit; }
        .d6-icon-btn:hover { color: #065d38; border-color: #bfe6cd; background: #f3fbf6; }
        .d6-icon-btn.on-brand { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.3); color: #ffffff; }
        .d6-icon-btn.on-brand:hover { background: rgba(255,255,255,0.3); }

        .d6-seg {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          padding: 3px;
          border-radius: 999px;
          background: #f4f4f5;
        }
        .d6-seg button {
          border: 0;
          background: transparent;
          color: #71717a;
          padding: 5px 12px;
          border-radius: 999px;
          transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
        }
        .d6-seg button:hover { color: #18181b; }
        .d6-seg button.active { background: #ffffff; color: #18181b; box-shadow: 0 1px 3px rgba(0,0,0,0.08) !important; }

        .d6-tooltip {
          background: #ffffff;
          border: 1px solid #e4e7eb;
          border-radius: 12px;
          padding: 8px 12px;
          min-width: 150px;
          box-shadow: 0 10px 24px -8px rgba(15, 23, 42, 0.18) !important;
        }

        .d6-meter-track { height: 6px; border-radius: 999px; background: #eef0f2; overflow: hidden; }
        .d6-meter-fill { height: 100%; border-radius: 999px; transition: width 0.9s cubic-bezier(0.16, 1, 0.3, 1); }

        .d6-list-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 8px;
          margin: 0 -8px;
          border-radius: 12px;
          cursor: pointer;
          transition: background 0.15s ease;
        }
        .d6-list-row:hover { background: #f6faf7; }
        .d6-list-row + .d6-list-row { border-top: 1px solid #f1f3f5; }


        .d6-gauge-seg { transition: opacity 0.2s ease; cursor: pointer; transform-origin: 100px 104px; animation: d6SegIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes d6SegIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
        .d6-gauge-legend { display: grid; gap: 6px; margin-top: 18px; padding-top: 14px; border-top: 1px solid #f1f3f5; }
        .d6-gauge-key {
          display: flex; flex-direction: column; align-items: center; gap: 1px; border: 0; background: transparent;
          padding: 6px 4px; border-radius: 10px; transition: background 0.15s ease; min-width: 0;
        }
        .d6-gauge-key:hover, .d6-gauge-key.on { background: #f6faf7; }
      `}</style>

      <PagePreloader visible={!pageReady} />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div id="kt_app_header" className="app-header" data-kt-sticky="true" data-kt-sticky-activate="{default: false, lg: true}" data-kt-sticky-name="app-header-sticky" data-kt-sticky-offset="{default: false, lg: '300px'}">
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6" id="kt_app_header_container">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2"></i>
                </div>
                <NavLink to="/dashboard6">
                  <img alt="Logo" src="/assets/media/white.png" className="h-30px" />
                </NavLink>
              </div>

              <div className="d-flex flex-stack flex-lg-row-fluid" id="kt_app_header_wrapper">
                <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3 mb-0" data-kt-swapper="1" data-kt-swapper-mode="{default: 'prepend', lg: 'prepend'}" data-kt-swapper-parent="{default: '#kt_app_content_container', lg: '#kt_app_header_wrapper'}">
                  <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 mb-2">
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1">
                      <NavLink to="/dashboard6" className="text-gray-700 text-hover-primary me-1"><i className="ki-outline ki-home text-gray-700 fs-6"></i></NavLink>
                    </li>
                    <li className="breadcrumb-item"><i className="ki-outline ki-right fs-7 text-gray-700 mx-n1"></i></li>
                    <li className="breadcrumb-item text-gray-600 fw-bold lh-1">Dashboard</li>
                  </ul>
                  <h1 className="text-gray-900 d6-page-title m-0">Dashboard</h1>
                </div>

                <div className="d-flex align-items-center gap-2">
                  {/* Search Bar with adjusted padding */}
                  <div ref={searchContainerRef} className="position-relative">
                    <div
                      className="d-flex align-items-center bg-white rounded-pill px-4 py-2 shadow-xs transition-all search-pill-wrapper"
                      style={{ minHeight: "35px", height: "35px", width: "300px", maxWidth: "100%" }}
                    >
                      <i className="ki-outline ki-magnifier fs-4 text-gray-400 me-2.5 flex-shrink-0"></i>
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="form-control form-control-flush border-0 bg-transparent p-0 fs-7 fw-medium text-gray-800 flex-grow-1"
                        placeholder="Search residents, units, pages..."
                        value={searchInputText}
                        onChange={(e) => setSearchInputText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleExecuteSearch();
                          } else if (e.key === "Escape") {
                            setIsSearchOpen(false);
                          }
                        }}
                        style={{ outline: "none", boxShadow: "none" }}
                      />
                      {searchInputText ? (
                        <button
                          type="button"
                          className="btn btn-icon btn-sm flex-shrink-0 p-0 ms-1"
                          style={{ width: "22px", height: "22px", background: "transparent", border: "none" }}
                          onClick={() => {
                            setSearchInputText("");
                            setExecutedSearchQuery("");
                            setIsSearchOpen(false);
                            searchInputRef.current?.focus();
                          }}
                          title="Clear search"
                        >
                          <i className="ki-outline ki-cross fs-5 text-gray-400"></i>
                        </button>
                      ) : (
                        <span className="badge badge-light-secondary fs-9 fw-bold text-gray-400 px-2 py-0.5 rounded-pill flex-shrink-0 ms-1">
                          ⌘K
                        </span>
                      )}
                    </div>

                    {/* Search Results Dropdown */}
                    {isSearchOpen && executedSearchQuery.trim() && (
                      <div
                        className="position-absolute end-0 bg-white rounded-4 border border-gray-100 py-2 mt-2 shadow-lg"
                        style={{
                          top: "100%",
                          zIndex: 1050,
                          boxShadow: "0 20px 45px -10px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(0,0,0,0.05)",
                          maxHeight: "450px",
                          overflowY: "auto",
                          width: "380px",
                          maxWidth: "92vw",
                        }}
                      >
                        {totalSearchMatches === 0 ? (
                          <div className="text-center py-6 px-4">
                            <i className="ki-outline ki-magnifier fs-2x text-gray-300 mb-2 mx-auto d-block"></i>
                            <div className="fs-7 fw-bold text-gray-800 mb-1">
                              No results for "{executedSearchQuery}"
                            </div>
                            <div className="fs-8 text-gray-400">
                              Try searching by another name, unit, or issue.
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex flex-column py-1">
                            {searchResults.tenants.length > 0 && (
                              <div className="mb-2">
                                <div className="fs-9 fw-bold text-gray-400 px-3 py-1 tracking-wider d-flex align-items-center justify-content-between">
                                  <span>Residents &amp; Tenants</span>
                                  <span className="badge badge-light-success fs-9 px-1.5 py-0.5">{searchResults.tenants.length}</span>
                                </div>
                                {searchResults.tenants.map((t) => (
                                  <button
                                    key={t.id || t.email}
                                    type="button"
                                    className="w-100 text-start btn btn-clean p-2.5 px-3 d-flex align-items-center justify-content-between bg-hover-light border-0"
                                    onClick={() => handleSelectTenantResult(t)}
                                  >
                                    <div className="d-flex align-items-center gap-2.5 min-w-0">
                                      <div className="symbol symbol-30px flex-shrink-0">
                                        <span className="symbol-label bg-light-success text-success fw-bold fs-8">
                                          {(t.firstName?.[0] || t.name?.[0] || "T").toUpperCase()}
                                        </span>
                                      </div>
                                      <div className="min-w-0">
                                        <div className="text-gray-900 fs-7 fw-bold text-truncate">
                                          {t.name || `${t.firstName || ""} ${t.lastName || ""}`.trim()}
                                        </div>
                                        <div className="text-gray-400 fs-8 text-truncate">
                                          {t.house || (t.buildingNumber ? `Unit ${t.buildingNumber}` : "Resident")} • {t.phoneNumber || t.email || "No contact"}
                                        </div>
                                      </div>
                                    </div>
                                    <span className={`badge ${t.isActivated ? "badge-light-success" : "badge-light-warning"} fs-9 px-2 py-0.5 flex-shrink-0 ms-2`}>
                                      {t.isActivated ? "Active" : "Inactive"}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}

                            {searchResults.requests.length > 0 && (
                              <div className="mb-2">
                                <div className="fs-9 fw-bold text-gray-400 px-3 py-1 tracking-wider d-flex align-items-center justify-content-between">
                                  <span>Maintenance Requests</span>
                                  <span className="badge badge-light-primary fs-9 px-1.5 py-0.5">{searchResults.requests.length}</span>
                                </div>
                                {searchResults.requests.map((r) => (
                                  <button
                                    key={r.id}
                                    type="button"
                                    className="w-100 text-start btn btn-clean p-2.5 px-3 d-flex align-items-center justify-content-between bg-hover-light border-0"
                                    onClick={() => handleSelectRequestResult(r)}
                                  >
                                    <div className="d-flex align-items-center gap-2.5 min-w-0">
                                      <div className="symbol symbol-30px flex-shrink-0">
                                        <span className="symbol-label bg-light-warning text-warning">
                                          <i className="ki-outline ki-wrench fs-6 me-2"></i>
                                        </span>
                                      </div>
                                      <div className="min-w-0">
                                        <div className="text-gray-900 fs-7 fw-bold text-truncate">
                                          {r.title || r.requestType || "Maintenance Request"}
                                        </div>
                                        <div className="text-gray-400 fs-8 text-truncate">
                                          {r.tenantName || "Resident"} • {r.category || "General"}
                                        </div>
                                      </div>
                                    </div>
                                    <span className="badge badge-light-danger fs-9 px-2 py-0.5 flex-shrink-0 ms-2">
                                      {r.priority || r.status || "Pending"}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}

                            {searchResults.properties.length > 0 && (
                              <div className="mb-2">
                                <div className="fs-9 fw-bold text-gray-400 px-3 py-1 tracking-wider d-flex align-items-center justify-content-between">
                                  <span>Properties &amp; Units</span>
                                  <span className="badge badge-light fs-9 px-1.5 py-0.5">{searchResults.properties.length}</span>
                                </div>
                                {searchResults.properties.map((p) => {
                                  const pIdx = (allHouseTypes || []).findIndex((h) => h.id === p.id);
                                  return (
                                    <button
                                      key={p.id}
                                      type="button"
                                      className="w-100 text-start btn btn-clean p-2.5 px-3 d-flex align-items-center justify-content-between bg-hover-light border-0"
                                      onClick={() => handleSelectPropertyResult(pIdx >= 0 ? pIdx : 0)}
                                    >
                                      <div className="d-flex align-items-center gap-2.5 min-w-0">
                                        <div className="symbol symbol-30px flex-shrink-0">
                                          <span className="symbol-label bg-light-primary text-primary">
                                            <i className="ki-outline ki-bank fs-6 me-2"></i>
                                          </span>
                                        </div>
                                        <div className="min-w-0">
                                          <div className="text-gray-900 fs-7 fw-bold text-truncate">
                                            {p.name || p.typeName}
                                          </div>
                                          <div className="text-gray-400 fs-8 text-truncate">
                                            {p.phaseName || "Estate Grounds"} • Spotlight View
                                          </div>
                                        </div>
                                      </div>
                                      <i className="ki-outline ki-right fs-7 text-gray-400 flex-shrink-0 ms-2"></i>
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            {searchResults.pages.length > 0 && (
                              <div>
                                <div className="fs-9 fw-bold text-gray-400 px-3 py-1 tracking-wider d-flex align-items-center justify-content-between">
                                  <span>System Pages</span>
                                  <span className="badge badge-light fs-9 px-1.5 py-0.5">{searchResults.pages.length}</span>
                                </div>
                                {searchResults.pages.map((pg) => (
                                  <button
                                    key={pg.path}
                                    type="button"
                                    className="w-100 text-start btn btn-clean p-2.5 px-3 d-flex align-items-center justify-content-between bg-hover-light border-0"
                                    onClick={() => handleSelectPageResult(pg.path)}
                                  >
                                    <div className="d-flex align-items-center gap-2.5 min-w-0">
                                      <div className="symbol symbol-30px flex-shrink-0">
                                        <span className="symbol-label bg-light text-gray-700">
                                          <i className={`ki-outline ${pg.icon} fs-6 me-2`}></i>
                                        </span>
                                      </div>
                                      <div className="min-w-0">
                                        <div className="text-gray-900 fs-7 fw-bold text-truncate">
                                          {pg.title}
                                        </div>
                                        <div className="text-gray-400 fs-8 text-truncate">
                                          {pg.desc}
                                        </div>
                                      </div>
                                    </div>
                                    <i className="ki-outline ki-right fs-7 text-gray-400 flex-shrink-0 ms-2"></i>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button type="button" className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => addTenantModal.show()}>
                    <i className="ki-outline ki-plus fs-6 me-sm-2 text-white"></i>
                    <span className="d-none d-sm-inline">Add New Tenant</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />

            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="mb-6 gsap-header">
                      <h1 className="text-gray-900 d6-greeting mb-1">
                        {greeting}, {adminFullName || "Admin"}
                      </h1>
                      <p className="text-gray-500 fs-7 mb-0">
                        Real-time operations, gate traffic and revenue performance
                      </p>
                    </div>

                    {/* Row A: KPI tiles (left) + Access Analytics (right) */}
                    <div className="row g-4 mb-5">
                      <div className="col-12 col-xl-5">
                        <div className="row g-4 h-100">
                          <div className="col-12 gsap-hero">
                            <div className="d6-kpi-brand d6-kpi p-6 h-100 d-flex flex-column justify-content-between">
                              <div className="d-flex align-items-start justify-content-between gap-3 position-relative" style={{ zIndex: 2 }}>
                                <div>
                                  <h3 className="d6-title mb-1 text-white">Rent Revenue</h3>
                                  <span className="fs-8 fw-medium text-white" style={{ opacity: 0.8 }}>Received from active leases</span>
                                </div>
                                <NavLink to="/rent-management" className="d6-icon-btn on-brand" title="Open rent management">
                                  <i className="ki-outline ki-arrow-up-right fs-5"></i>
                                </NavLink>
                              </div>

                              <div className="position-relative my-3" style={{ zIndex: 2 }}>
                                <div className="d6-num text-white">
                                  ₦{stats.totalRentCollected.toLocaleString()}
                                </div>
                              </div>

                              <div className="position-relative" style={{ zIndex: 2 }}>
                                <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                                  <span className="fs-8 fw-bold text-white">{rentCollectedPct}% collected</span>
                                  <span className="fs-8 fw-semibold text-white" style={{ opacity: 0.85 }}>
                                    ₦{stats.pendingRent.toLocaleString()} pending
                                  </span>
                                </div>
                                <div style={{ height: 6, borderRadius: 999, background: "rgba(0,0,0,0.2)", overflow: "hidden" }}>
                                  <div
                                    style={{
                                      width: `${rentCollectedPct}%`,
                                      height: "100%",
                                      borderRadius: 999,
                                      background: "#ffffff",
                                      transition: "width 0.9s cubic-bezier(0.16, 1, 0.3, 1)",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-6 gsap-hero">
                            <div className="d6-card d6-kpi p-6 h-100 d-flex flex-column justify-content-between">
                              <div className="d-flex align-items-start justify-content-between gap-2">
                                <div>
                                  <h3 className="text-gray-900 d6-title mb-1">Properties Managed</h3>
                                  <span className="text-gray-400 fs-8">Total building units in estate</span>
                                </div>
                                {!isPhaseAdmin() && (
                                  <NavLink to="/estate-setup" className="d6-icon-btn" title="Open estate setup">
                                    <i className="ki-outline ki-arrow-up-right fs-5"></i>
                                  </NavLink>
                                )}
                              </div>
                              <div className="text-gray-900 d6-num my-3">
                                {stats.propertiesManaged} <span className="d6-num-suffix text-gray-400 ms-1">Units</span>
                              </div>
                              <div>
                                <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                                  <span className="fw-semibold fs-8 text-gray-600">
                                    <span className="fw-bold text-gray-900">{occupancyCurrentData.vacant}</span> Vacant Units
                                  </span>
                                  <span className="fs-8 fw-semibold text-gray-400">{occupancyCurrentData.assignedPct}%</span>
                                </div>
                                <div className="d6-meter-track">
                                  <div className="d6-meter-fill" style={{ width: `${occupancyCurrentData.assignedPct}%`, background: SERIES[0] }} />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-6 gsap-hero">
                            <div className="d6-card d6-kpi p-6 h-100 d-flex flex-column justify-content-between">
                              <div className="d-flex align-items-start justify-content-between gap-2">
                                <div>
                                  <h3 className="text-gray-900 d6-title mb-1">Active Tenants</h3>
                                  <span className="text-gray-400 fs-8">Registered estate residents</span>
                                </div>
                                <NavLink to="/tenants" className="d6-icon-btn" title="Open tenant management">
                                  <i className="ki-outline ki-arrow-up-right fs-5"></i>
                                </NavLink>
                              </div>
                              <div className="text-gray-900 d6-num my-3">
                                {stats.totalTenants} <span className="d6-num-suffix text-gray-400 ms-1">Residents</span>
                              </div>
                              {!isPhaseAdmin() && (
                              <div>
                                <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                                  <span className="fw-semibold fs-8 text-gray-600">
                                    <span className="fw-bold text-gray-900">{notActivatedCount}</span> Inactive Tenants
                                  </span>
                                  <span className="fs-8 fw-semibold text-gray-400">{activatedPct}%</span>
                                </div>
                                <div className="d6-meter-track">
                                  <div className="d6-meter-fill" style={{ width: `${activatedPct}%`, background: SERIES[0] }} />
                                </div>
                              </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-xl-7 gsap-card">
                        <div className="d6-card p-6 h-100 d-flex flex-column">
                          <CardHead
                            title="Access Analytics"
                            subtitle={
                              trendPeriod === "Today"
                                ? "Gate check-ins and check-outs by hour, today"
                                : trendPeriod === "Last 7 Days"
                                ? "Gate check-ins and check-outs, last 7 days"
                                : "Gate check-ins and check-outs by week, last 4 weeks"
                            }
                            className="mb-3"
                          >
                            <SegTabs
                              options={[
                                { value: "Today", label: "Today" },
                                { value: "Last 7 Days", label: "Week" },
                                { value: "Last 30 Days", label: "Month" },
                              ]}
                              value={trendPeriod}
                              onChange={setTrendPeriod}
                            />
                            <NavLink to="/estate-access" className="d6-icon-btn" title="Open access logs">
                              <i className="ki-outline ki-arrow-up-right fs-5"></i>
                            </NavLink>
                          </CardHead>

                          <div className="d-flex flex-wrap align-items-end gap-6 mb-2">
                            {[
                              { key: "in", label: "Clock In", value: accessTotals.totalIn, color: SERIES[0], on: showClockIn, toggle: () => setShowClockIn((p) => !p) },
                              { key: "out", label: "Clock Out", value: accessTotals.totalOut, color: CLOCK_OUT, dashed: true, on: showClockOut, toggle: () => setShowClockOut((p) => !p) },
                            ].map((s) => (
                              <button
                                key={s.key}
                                type="button"
                                className="border-0 bg-transparent p-0 text-start"
                                onClick={s.toggle}
                                title={s.on ? `Hide ${s.label}` : `Show ${s.label}`}
                                style={{ opacity: s.on ? 1 : 0.4, transition: "opacity 0.2s ease" }}
                              >
                                <div className="d-flex align-items-center gap-2 fs-8 fw-semibold text-gray-500 mb-1">
                                  <span style={{ width: 16, height: 0, borderTop: `3px ${s.dashed ? "dashed" : "solid"} ${s.color}`, display: "inline-block" }} />
                                  {s.label}
                                </div>
                                <div className="text-gray-900 d6-num-md">
                                  {s.value.toLocaleString()}
                                </div>
                              </button>
                            ))}
                            <div className="ps-6 border-start border-gray-200">
                              <div className="fs-8 fw-semibold text-gray-500 mb-1">Still on site</div>
                              <div className="text-gray-900 d6-num-md">
                                {accessTotals.onSite.toLocaleString()}
                              </div>
                            </div>
                            {accessTotals.peak && (
                              <div className="ms-auto text-end d-none d-md-block">
                                <div className="fs-8 fw-semibold text-gray-500 mb-1">Busiest {trendPeriod === "Today" ? "hour" : trendPeriod === "Last 7 Days" ? "day" : "week"}</div>
                                <div className="fs-6 fw-bold text-gray-900">{accessTotals.peak.fullName || accessTotals.peak.name}</div>
                              </div>
                            )}
                          </div>

                          <div className="mt-auto" style={{ width: "100%", height: 270 }} tabIndex={-1}>
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={accessTrendData} margin={{ top: 12, right: 8, left: -18, bottom: 0 }}>
                                <defs>
                                  <linearGradient id="d6InWash" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={SERIES[0]} stopOpacity={0.16} />
                                    <stop offset="100%" stopColor={SERIES[0]} stopOpacity={0} />
                                  </linearGradient>
                                        </defs>
                                <CartesianGrid vertical={false} stroke="#f1f3f5" />
                                <XAxis
                                  dataKey="name"
                                  axisLine={false}
                                  tickLine={false}
                                  interval={trendPeriod === "Today" ? 3 : 0}
                                  tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 500 }}
                                  dy={6}
                                />
                                <YAxis axisLine={false} tickLine={false} allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#d4d4d8", strokeWidth: 1 }} />
                                <Area
                                  name="Clock In"
                                  type="monotone"
                                  dataKey="clockIn"
                                  stroke={SERIES[0]}
                                  strokeWidth={2}
                                  fill="url(#d6InWash)"
                                  hide={!showClockIn}
                                  dot={false}
                                  activeDot={{ r: 5, fill: SERIES[0], stroke: "#ffffff", strokeWidth: 2 }}
                                />
                                <Area
                                  name="Clock Out"
                          type="monotone"
                          dataKey="clockOut"
                          stroke={CLOCK_OUT}
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          fill="none"
                          hide={!showClockOut}
                          dot={false}
                          activeDot={{ r: 5, fill: CLOCK_OUT, stroke: "#ffffff", strokeWidth: 2 }}
                        />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Row B: Property spotlight + Occupancy & accounts */}
                    <div className="row g-4 mb-5">
                      <div className="col-12 col-xl-7 gsap-card">
                        <div
                          className="card card-flush border-0 spotlight-card position-relative overflow-hidden shadow-xs h-100"
                          style={{ borderRadius: "16px", minHeight: "360px" }}
                        >
                          {currentHouse && (
                            <img
                              key={houseAnimKey}
                              src={currentHouse.image}
                              alt={currentHouse.name}
                              className="spotlight-bg-img ken-burns-hero"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/assets/media/zangon/3bedroomsbungalo.jpg";
                              }}
                            />
                          )}
                          <div
                            className="position-absolute"
                            style={{
                              inset: 0,
                              background:
                                "linear-gradient(90deg, rgba(6,40,24,0.85) 0%, rgba(6,40,24,0.45) 55%, rgba(6,40,24,0.15) 100%)",
                              zIndex: 1,
                            }}
                          />

                          <div
                            className="position-relative p-6 p-lg-8 d-flex flex-column justify-content-between h-100"
                            style={{ zIndex: 2 }}
                          >
                            {/* Top Header Section */}
                            <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                              <div className="d-flex flex-column gap-2" style={{ maxWidth: "560px" }}>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <span className="badge bg-white bg-opacity-25 text-white border border-white border-opacity-25 px-3 py-1.5 fs-8 fw-bold rounded-pill">
                                    {currentHouse?.phaseName || "Phase 1"}
                                  </span>
                                  <span className="badge bg-success bg-opacity-90 text-white px-3 py-1.5 fs-8 fw-bold rounded-pill">
                                    {currentHouse?.totalUnits
                                      ? `${currentHouse.totalUnits} Units`
                                      : currentHouse?.buildingNumber
                                      ? String(currentHouse.buildingNumber).toLowerCase().includes("unit")
                                        ? currentHouse.buildingNumber
                                        : `Unit ${currentHouse.buildingNumber}`
                                      : "Unit 1"}
                                  </span>
                                </div>
                                <h2 className="d6-hero-title text-white mb-1">
                                  {currentHouse?.name || currentHouse?.typeName || "Property Spotlight"}
                                </h2>
                                <span className="text-white text-opacity-80 fs-7">
                                  Featured estate building profile with registered active tenants
                                </span>
                              </div>
                            </div>

                            {/* Bottom Footer Section */}
                            <div className="d-flex align-items-end justify-content-between flex-wrap gap-3 pt-6">
                              <div
                                key={activeHouseIndex}
                                className="spotlight-resident-pill d-flex align-items-center resident-pill-anim"
                                style={{
                                  padding: currentResidents.avatars.length > 0 ? "6px 20px 6px 8px" : "10px 18px",
                                }}
                              >
                                {currentResidents.avatars.length > 0 ? (
                                  <>
                                    <div className="d-flex align-items-center">
                                      {currentResidents.avatars.map((res, i) => (
                                        <img
                                          key={`${activeHouseIndex}-${i}-${res.id || res.imageUrl}`}
                                          src={res.imageUrl}
                                          alt={res.name}
                                          title={res.name}
                                          data-bs-toggle="tooltip"
                                          data-bs-placement="top"
                                          data-bs-title={res.name}
                                          className="rounded-circle"
                                          style={{
                                            width: "36px",
                                            height: "36px",
                                            objectFit: "cover",
                                            borderRadius: "50%",
                                            border: "2px solid #ffffff",
                                            marginLeft: i > 0 ? "-10px" : "0",
                                            zIndex: 10 - i,
                                            cursor: "pointer",
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                          }}
                                        />
                                      ))}
                                    </div>
                                    <span className="text-white fw-bold fs-7 ms-3">
                                      {currentResidents.othersCount > 0
                                        ? `+${currentResidents.othersCount} other residents`
                                        : `${currentResidents.totalCount} Resident${
                                            currentResidents.totalCount === 1 ? "" : "s"
                                          }`}
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-white text-opacity-80 fs-7 fw-semibold">
                                    {currentResidents.totalCount > 0
                                      ? `${currentResidents.totalCount} Resident${
                                          currentResidents.totalCount === 1 ? "" : "s"
                                        }`
                                      : "No resident photos yet"}
                                  </span>
                                )}
                              </div>

                              {/* Navigation buttons moved here in place of the Active Occupancy badge */}
                              <div className="d-flex gap-2 flex-shrink-0 align-items-center">
                                <button
                                  type="button"
                                  className="spotlight-nav-btn"
                                  title="Previous"
                                  onClick={() => {
                                    setActiveHouseIndex(
                                      (p) => (p - 1 + (allHouseTypes.length || 1)) % (allHouseTypes.length || 1)
                                    );
                                    setHouseAnimKey((k) => k + 1);
                                  }}
                                >
                                  <i className="ki-outline ki-arrow-left fs-5 text-white"></i>
                                </button>
                                <button
                                  type="button"
                                  className="spotlight-nav-btn"
                                  title="Next"
                                  onClick={() => {
                                    setActiveHouseIndex((p) => (p + 1) % (allHouseTypes.length || 1));
                                    setHouseAnimKey((k) => k + 1);
                                  }}
                                >
                                  <i className="ki-outline ki-arrow-right fs-5 text-white"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-xl-5 gsap-card">
                        <div className="d6-card p-6 h-100 d-flex flex-column">
                          <CardHead
                            title="Occupancy & Accounts"
                            subtitle={
                              occupancyTab === "current"
                                ? "Occupied vs vacant units"
                                : occupancyTab === "phase"
                                ? "Units across estate phases"
                                : "App activation across residents"
                            }
                          >
                            <SegTabs
                              options={[
                                { value: "current", label: "Units" },
                                { value: "phase", label: "Phases" },
                                ...(isPhaseAdmin() ? [] : [{ value: "accounts", label: "Accounts" }]),
                              ]}
                              value={occupancyTab}
                              onChange={setOccupancyTab}
                            />
                          </CardHead>

                          <div className="my-auto pt-2">
                            {occupancyTab === "current" && (
                              <SectionGauge
                                key="current"
                                segments={[
                                  { label: "Occupied", value: occupancyCurrentData.assigned, color: SERIES[0] },
                                  { label: "Vacant", value: occupancyCurrentData.vacant, color: GAUGE_NEUTRAL },
                                ]}
                                center={() => (
                                  <>
                                    <span className="text-gray-900 d6-num">
                                      {occupancyCurrentData.assignedPct}%
                                    </span>
                                    <span className="text-gray-400 fs-8 mt-1">of {occupancyCurrentData.total} units occupied</span>
                                  </>
                                )}
                              />
                            )}
                            {occupancyTab === "phase" && (
                              <SectionGauge
                                key="phase"
                                segments={occupancyPhaseData.items.map((p) => ({ label: p.name, value: p.count, color: p.color }))}
                                center={() => (
                                  <>
                                    <span className="text-gray-900 d6-num">
                                      {occupancyPhaseData.total}
                                    </span>
                                    <span className="text-gray-400 fs-8 mt-1">units across {occupancyPhaseData.items.length} phases</span>
                                  </>
                                )}
                              />
                            )}
                            {occupancyTab === "accounts" && (
                              <SectionGauge
                                key="accounts"
                                segments={[
                                  { label: "Activated", value: activatedCount, color: SERIES[0] },
                                  { label: "Not activated", value: notActivatedCount, color: GAUGE_NEUTRAL },
                                ]}
                                center={() => (
                                  <>
                                    <span className="text-gray-900 d6-num">
                                      {activatedPct}%
                                    </span>
                                    <span className="text-gray-400 fs-8 mt-1">of {totalTenantsCount} accounts activated</span>
                                  </>
                                )}
                              />
                            )}
                          </div>

                        </div>
                      </div>
                    </div>

                    {/* Row D: Rent due + tenant requests */}
                    <div className="row g-4 mb-5">
                      <div className="col-12 col-lg-6 gsap-card">
                        <div className="d6-card p-6 h-100 d-flex flex-column">
                          <CardHead title="Rent Due Soon" subtitle="Track rent status and send reminders.">
                            <button type="button" className="btn btn-light-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={handleRemindAll}>
                              <i className="ki-outline ki-notification-bing fs-6 me-2"></i>Remind Tenants
                            </button>
                          </CardHead>

                          <div className="d-flex flex-column mh-500px h-500px overflow-y-auto pe-1">
                            {isLoadingTenants ? (
                              [1, 2, 3, 4].map((n) => <RentListSkeleton key={n} />)
                            ) : sortedRentTenants.length === 0 ? (
                              <div className="text-center py-15 my-auto">
                                <img src={getEmptyStateImg()} className="h-200px mb-4 object-fit-contain" alt="No data" />
                                <div className="d6-title text-gray-900 mb-2">No rent data available.</div>
                                <div className="fs-6 text-gray-500">Add tenants to start tracking rent.</div>
                              </div>
                            ) : (
                              sortedRentTenants.map((t, idx) => {
                                const meta = getDueMeta(today, t.expiryISO);
                                return (
                                  <div
                                    key={t.cardId || `${t.id}_${idx}`}
                                    className="d-flex align-items-center mb-3 py-6 px-6 d6-list-card border border-hover-success cursor-pointer transition-all"
                                    style={{ minHeight: "90px" }}
                                    onClick={() => handleTenantClick(t)}
                                  >
                                    <div className="symbol symbol-45px symbol-circle me-5 flex-shrink-0">
                                      {t.imageUrl ? (
                                        <img src={t.imageUrl} alt={t.name} />
                                      ) : (
                                        <span className={`symbol-label fw-bold ${t.avatarClass || "bg-light-success text-success"}`}>{t.initials}</span>
                                      )}
                                    </div>
                                    <div className="d-flex flex-column flex-grow-1 min-w-0">
                                      <span className="fs-6 fw-bold text-gray-900 text-truncate">{t.name}</span>
                                      <div className="text-gray-500 fs-7 text-truncate">{t.house}</div>
                                    </div>
                                    <div className="d-flex gap-8 text-end ms-6 flex-shrink-0">
                                      <div>
                                        <div className="text-gray-500 fs-8">Amount Due</div>
                                        <div className="fw-semibold text-gray-900">{t.amount}</div>
                                      </div>
                                      <div>
                                        <div className="text-gray-500 fs-8">Rent Expiry</div>
                                        <div className={`fw-semibold ${meta.expiryClass}`}>{t.expiryLabel}</div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 gsap-card">
                        <div className="d6-card p-6 h-100 d-flex flex-column">
                          <CardHead title="Tenant Requests" subtitle="View and manage requests from residents.">
                            {!isPhaseAdmin() && (
                              <button type="button" className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => categoriesModal.show()}>
                                <i className="ki-outline ki-setting-2 fs-6 me-2"></i>Categories
                              </button>
                            )}
                            <NavLink to="/tenant-requests" className="btn btn-light-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold">
                              <i className="ki-outline ki-eye fs-6 me-2"></i>View All
                            </NavLink>
                          </CardHead>

                          <div className="d-flex flex-column mh-500px h-500px overflow-y-auto pe-1">
                            {isLoadingRequests ? (
                              [1, 2, 3].map((n) => <RequestListSkeleton key={n} />)
                            ) : activeRequests.length === 0 ? (
                              <div className="text-center py-15 my-auto">
                                <img src={getEmptyStateImg()} className="h-200px mb-4 object-fit-contain" alt="All caught up" />
                                <div className="d6-title text-gray-900 mb-2">You are all caught up.</div>
                                <div className="fs-6 text-gray-500">No open requests. Good Job!</div>
                              </div>
                            ) : (
                              activeRequests.map((r) => {
                                const st = getRequestStatusMeta(r);
                                const imgUrl =
                                  (Array.isArray(r.uploadedFileUrls) && r.uploadedFileUrls[0]) ||
                                  r.buildingImageUrl ||
                                  getHouseImage(r.typeName || r.buildingNumber || r.phaseName);
                                return (
                                  <div
                                    key={r.id}
                                    className={`d-flex align-items-center mb-3 py-6 px-6 d6-list-card border border-gray-200 ${st.hoverBorder} cursor-pointer transition-all position-relative overflow-hidden`}
                                    style={{ minHeight: "90px" }}
                                    onClick={() => handleRequestClick(r)}
                                  >
                                    <div className={`position-absolute top-0 start-0 h-100 w-4px ${st.bar}`}></div>
                                    <div className="symbol symbol-45px symbol-circle me-5 ms-2 flex-shrink-0 border border-gray-200 overflow-hidden">
                                      <img
                                        src={imgUrl}
                                        alt={r.title || "Request image"}
                                        className="w-45px h-45px rounded-circle object-fit-cover"
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = "/assets/media/zangon/3bedroomsbungalo.jpg";
                                        }}
                                      />
                                    </div>
                                    <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                                      <span className="fs-6 fw-bold text-gray-900 text-truncate">{r.title}</span>
                                      <div className="text-gray-500 fs-7 text-truncate">
                                        {r.buildingNumber || "Unassigned"} · {r.category}
                                      </div>
                                    </div>
                                    <div className="text-end ms-6 flex-shrink-0">
                                      <div className="text-gray-500 fs-8">Status</div>
                                      <div className={`fw-semibold ${st.text}`}>{st.label}</div>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div id="kt_app_footer" className="app-footer">
                  <div className="app-container container-fluid d-flex justify-content-center align-items-center py-3">
                    <div className="text-gray-900 text-center">
                      <span className="text-muted fw-semibold me-1">2026&copy;</span>
                      <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">Powered by Pixels Solutions Limited</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="kt_scrolltop" className="scrolltop bg-success d-flex align-items-center justify-content-center" data-kt-scrolltop="true">
          <i className="ki-outline ki-arrow-up fs-3 text-white"></i>
        </div>

        <TenantDetailModal
          modalRef={tenantDetailModalRef}
          tenant={selectedTenant}
          today={today}
          onClose={() => setSelectedTenant(null)}
          onViewFull={(t) => navigate(`/rent-details/${t.id}`)}
        />

        <RequestDetailModal
          modalRef={requestDetailModalRef}
          estateId={estateId}
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onStatusChange={(reqId, newStatus) =>
            setRequests((prev) => prev.map((r) => (r.id === reqId ? { ...r, status: newStatus } : r)))
          }
        />

        <AddTenantModal
          modalRef={addTenantModalRef}
          estateId={estateId}
          adminId={session?.userId || ""}
          onClose={() => {}}
          onSuccess={() => fetchDashboardData({ silent: true })}
        />

        <DownloadTenantModal
          modalRef={downloadTenantModalRef}
          tenants={allEstateTenants}
        />

        <ManageCategoriesModal
          modalRef={categoriesModalRef}
          estateId={estateId}
        />
      </div>
    </>
  );
}